// features/projects/templates/continuum/simulation.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM SIMULATION — Tab 3
//
// Port of continuum_simulation.html. Two screens with an inner
// switcher (Setup ↔ Findings). Uses the cool Continuum palette
// (C in tokens.js) — distinct from the green Signal palette
// used by the Dashboard tab.
//
// SETUP screen — 3 columns:
//   Pipeline   → 6 steps with status (done / active / pending)
//   Code view  → 4 tabs (extractor.py / compare.py / run / How it works)
//   Datasets   → 9 dataset cards grouped by Ecological / Human
//
// FINDINGS screen — main + sidebar:
//   Main      → Run metadata strip + 5 finding cards
//                (eco vs human distribution bars, p-values,
//                 plain-English finding, optional caveat)
//   Sidebar   → Summary Matrix · Shifts Reading · Run History ·
//                Paper Readiness checklist
//
// All data is mocked verbatim from the source HTML so the visuals
// are pixel-faithful. Live wiring lands incrementally as the
// simulation backend comes together over the 6-month build.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { C, FONT as F } from './tokens';
import { DYNAMICS } from '../../../../lib/continuum/simulation/methodology';

// ─── Mock data — verbatim from continuum_simulation.html ──────

const PIPELINE = [
  { num: '✓', state: 'done',    label: 'Step 1 · Complete', name: 'Load ecological data',
    detail: 'IUCN Red List + Living Planet Index loaded into PostgreSQL.',
    metric: { val: 187, color: C.sage, lbl: 'documented collapse events' } },
  { num: '✓', state: 'done',    label: 'Step 2 · Complete', name: 'Load human system data',
    detail: 'UNESCO languages + US Census + FDIC + Crunchbase + IMF loaded.',
    metric: { val: 312, color: C.sage, lbl: 'human system events' } },
  { num: '3', state: 'active',  label: 'Step 3 · Active',   name: 'Extract ratio measurements',
    detail: 'Five extractor functions running on both datasets. MVP Threshold · Keystone Cascade · Refugia · Flexibility · Recovery Asymmetry',
    metric: { val: '5 of 5', color: C.gold, lbl: 'extractors complete' } },
  { num: '4', state: 'active',  label: 'Step 4 · Active',   name: 'Statistical comparison',
    detail: 't-tests and effect sizes computed for all five dynamics.',
    metric: { val: '5 of 5', color: C.gold, lbl: 'comparisons complete' } },
  { num: '5', state: 'pending', label: 'Step 5 · Pending',  name: 'Write findings to database',
    detail: 'Results write to dynamic_comparisons table. Shift Notes auto-updated.', metric: null },
  { num: '6', state: 'pending', label: 'Step 6 · Pending',  name: 'Generate Shift finding notes',
    detail: 'Plain English findings created in The Continuum Notes tab.', metric: null },
];

const CODE_TABS = [
  { id:'extract',  label:'extractor.py' },
  { id:'compare',  label:'compare.py' },
  { id:'run',      label:'run_simulation.py' },
  { id:'what',     label:'How it works' },
];

const CODE_FILES = {
  extract: {
    narr: 'One function per dynamic. Each reads from the database and returns a list of measured ratios. The same function runs on ecological data and human system data — the comparison happens in compare.py.',
    file: 'extractor.py',
    code: `# ── Dynamic 1: MVP Threshold ────────────────────────
def extract_mvp_ratios(conn, dataset_type):
    """
    For each collapse event in dataset:
    threshold_pct = threshold_population / peak_population
    Returns list of {ratio, outcome, system, source}
    """
    query = """
        SELECT system_name, peak_value,
               threshold_value, outcome, dataset_source
        FROM human_system_events
        WHERE dynamic_id = 'mvp_threshold'
          AND system_type = %s
          AND threshold_value IS NOT NULL
          AND peak_value > 0
    """
    cur = conn.cursor()
    cur.execute(query, (dataset_type,))
    rows = cur.fetchall()

    ratios = []
    for row in rows:
        name, peak, thresh, outcome, source = row
        ratio = thresh / peak
        ratios.append({
            'ratio': round(ratio, 4),
            'outcome': outcome,
            'system': name,
            'source': source
        })
    return ratios

# ── Dynamic 5: Recovery Asymmetry ───────────────────
def extract_asymmetry_ratios(conn, dataset_type):
    """
    asymmetry_ratio = collapse_rate / recovery_rate
    Expected > 1.0 (faster to fall than recover)
    """
    query = """
        SELECT system_name, collapse_rate,
               recovery_rate, outcome, dataset_source
        FROM human_system_events
        WHERE dynamic_id = 'recovery_asymmetry'
          AND system_type = %s
          AND collapse_rate IS NOT NULL
          AND recovery_rate IS NOT NULL
          AND recovery_rate > 0
    """
    cur = conn.cursor()
    cur.execute(query, (dataset_type,))
    rows = cur.fetchall()

    ratios = []
    for row in rows:
        name, cr, rr, outcome, source = row
        asymmetry = cr / rr
        ratios.append({
            'ratio': round(asymmetry, 3),
            'outcome': outcome,
            'system': name,
            'source': source
        })
    return ratios`,
  },
  compare: {
    narr: 'Takes two lists of ratios — ecological and human — and runs a t-test. Returns the interpretation, effect size, and plain English finding. This is the core of the simulation.',
    file: 'compare.py',
    code: `from scipy import stats
import numpy as np

def compare_dynamics(eco_ratios, human_ratios, name):
    eco   = [r['ratio'] for r in eco_ratios]
    human = [r['ratio'] for r in human_ratios]

    # Statistical test
    t, p = stats.ttest_ind(eco, human)

    # Effect size (Cohen's d)
    pooled_std = np.std(eco + human)
    d = (np.mean(eco) - np.mean(human)) / pooled_std

    # Interpretation
    if   p > 0.05:               verdict = 'PARALLEL'
    elif abs(d) < 0.3:           verdict = 'NEAR-PARALLEL'
    else:                        verdict = 'DIVERGENT'

    # Confidence level
    n_min = min(len(eco), len(human))
    conf  = 'HIGH' if n_min > 30 else \\
            'MEDIUM' if n_min > 15 else 'LOW'

    return {
        'dynamic':      name,
        'eco_mean':    round(np.mean(eco),  3),
        'eco_std':     round(np.std(eco),   3),
        'eco_n':       len(eco),
        'human_mean':  round(np.mean(human), 3),
        'human_std':   round(np.std(human),  3),
        'human_n':     len(human),
        'p_value':     round(p, 4),
        'effect_size': round(d, 3),
        'verdict':     verdict,
        'confidence':  conf
    }`,
  },
  run: {
    narr: 'The entry point. Run this file. It connects to the database, runs all five extractors on both datasets, compares them, stores the results, and prints the findings to console. Takes under 10 seconds.',
    file: 'run_simulation.py',
    code: `import psycopg2
from extractor import (extract_mvp_ratios,
    extract_keystone_ratios, extract_refugia_ratios,
    extract_flexibility_ratios, extract_asymmetry_ratios)
from compare import compare_dynamics
from datetime import datetime

# ── Connect ─────────────────────────────────────────
conn = psycopg2.connect(
    dbname="continuum", user="tim",
    host="localhost", port=5432
)

# ── Five dynamics ───────────────────────────────────
DYNAMICS = [
    ('mvp_threshold',      extract_mvp_ratios),
    ('keystone_cascade',   extract_keystone_ratios),
    ('refugia',            extract_refugia_ratios),
    ('behavioral_flex',    extract_flexibility_ratios),
    ('recovery_asymmetry', extract_asymmetry_ratios),
]

results = []
for dyn_id, extractor in DYNAMICS:
    eco   = extractor(conn, 'ecological')
    human = extractor(conn, 'human')
    r     = compare_dynamics(eco, human, dyn_id)
    results.append(r)
    # Store to database
    store_result(conn, r)
    # Print to console
    print(f"{r['dynamic']:25} | {r['verdict']:15} | p={r['p_value']} | conf={r['confidence']}")

conn.close()
print(f"\\nSimulation complete: {datetime.now()}")`,
  },
};

const HOW_IT_WORKS = [
  {
    eyebrow: 'The Simulation — Plain English',
    body: [
      { kind:'p', text:'The simulation asks one question five times: do ecological systems and human systems follow the same mathematical pattern for this dynamic?', strong:'do ecological systems and human systems follow the same mathematical pattern for this dynamic?' },
      { kind:'p', text:'For each dynamic, it measures the same ratio in two datasets — one biological, one human. Then it tests whether those measurements are statistically different.' },
      { kind:'rule', cls:'parallel',  hl:'If p > 0.05',                  text:' — the two distributions are not statistically distinguishable. The dynamic operates the same way across systems. PARALLEL.' },
      { kind:'rule', cls:'near',      hl:"If p < 0.05 and Cohen's d < 0.3", text:' — statistically different but practically small. NEAR-PARALLEL.' },
      { kind:'rule', cls:'divergent', hl:"If p < 0.05 and d > 0.3",       text:" — the systems genuinely behave differently on this dimension. DIVERGENT. This is the most interesting finding — it tells us something unique about that domain." },
    ],
  },
  {
    eyebrow: 'What makes a finding trustworthy',
    body: [
      { kind:'p', strong:'Sample size', text: ' — the more events in each dataset, the more reliable the finding. LOW confidence below 15 events per side. HIGH confidence above 30.' },
      { kind:'p', strong:'The caution', text: ' — correlation is not causation. Similar ratios across systems do not prove the same mechanism is operating. They suggest it. That is a hypothesis, not a proof. The paper frames it as such.' },
      { kind:'p', strong:'The value',   text: ' — finding that the Recovery Asymmetry Ratio is statistically similar across 187 species collapses and 312 human system collapses is a meaningful empirical finding — regardless of whether the underlying mechanism is identical.' },
    ],
  },
];

const DATASETS = [
  { group:'Ecological — 187 events', items: [
    { state:'live',    name:'IUCN Red List',         n:124,  desc:'Species collapse and recovery events. Population at tipping point documented.', dyns:['mvp','rec'] },
    { state:'live',    name:'Living Planet Index',   n:63,   desc:'32K vertebrate population trends. 50-year longitudinal data.',                   dyns:['flex','key'] },
  ]},
  { group:'Human Systems — 312 events', items: [
    { state:'live',    name:'UNESCO Languages',      n:47,   desc:'Speaker count at peak vs threshold for 7,000 languages tracked.', dyns:['mvp','ref'] },
    { state:'live',    name:'US Census — Communities', n:89, desc:'County-level population decline 1950-2024. Irreversible decline threshold documented.', dyns:['mvp','key'] },
    { state:'live',    name:'FDIC Bank Closures',    n:44,   desc:'1,500+ community bank closures with county economic outcomes.', dyns:['key','rec'] },
    { state:'live',    name:'IMF Financial Crises',  n:38,   desc:'150+ crises with collapse duration and recovery duration.', dyns:['rec'] },
    { state:'live',    name:'Fortune 500 1955-2024', n:56,   desc:'Business model diversification vs survival duration. 88% no longer exist.', dyns:['flex'] },
    { state:'partial', name:'Seshat Global History', n:38,   desc:'400+ societies over 10,000 years. Partial data use agreement pending.', dyns:['ref','flex'] },
    { state:'pending', name:'Crunchbase Startups',   n:'—',  desc:'25K startup failures. User count at failure vs founding. Downloading.', dyns:['mvp'] },
  ]},
];

const DYN_BADGES = {
  mvp:  { label:'MVP Threshold',     color:C.gold },
  key:  { label:'Keystone Cascade',  color:C.terra },
  ref:  { label:'Refugia',           color:C.purple },
  flex: { label:'Behavioral Flex',   color:C.teal },
  rec:  { label:'Recovery Asymmetry',color:C.amber },
};

// RUN_META, FINDINGS, SUMMARY_MATRIX all removed — derived from real
// continuum_runs + continuum_comparisons data via the FindingsScreen
// + adaptComparisonToCard adapter below.

/* eslint-disable no-unused-vars */
const _LEGACY_FINDINGS_REMOVED = [
  {
    num:'01', name:'Minimum Viable Population Threshold', verdict:'PARALLEL',     conf:'HIGH confidence',
    eco:  { vals:[2,4,8,14,22,26,20,14,8,4,2],   mean:'0.18',  lbl:'mean threshold ratio',  n:'n = 124 events' },
    hum:  { vals:[1,3,7,12,20,28,22,14,8,4,1],   mean:'0.21',  lbl:'mean threshold ratio',  n:'n = 136 events' },
    delta:'Δ 0.03',  deltaColor:null,
    stats:[
      { lbl:'p-value',     val:'0.34',     cls:'good' },
      { lbl:"Cohen's d",   val:'0.11',     cls:null },
      { lbl:'eco mean',    val:'0.18 ± 0.06', cls:null },
      { lbl:'human mean',  val:'0.21 ± 0.07', cls:null },
    ],
    finding: { lbl:'Plain English Finding', text:'A species population that falls below 18% of its historical peak rarely recovers. A community, language, or organization that falls below 21% of its peak rarely recovers. The difference of 3 percentage points is not statistically meaningful — the threshold operates at the same level across biological and human systems.' },
    caveat: 'Correlation not causation. Similar ratios suggest the same underlying dynamic — not proof of identical mechanisms. Causal claims require experimental validation beyond this observational data.',
  },
  {
    num:'02', name:'Keystone Cascade', verdict:'PARALLEL', conf:'MEDIUM confidence',
    eco: { vals:[3,6,12,18,22,18,12,6,2,1,0], mean:'3.2×', lbl:'cascade amplification', n:'n = 63 events' },
    hum: { vals:[2,5,10,16,22,20,14,7,3,1,0], mean:'2.8×', lbl:'cascade amplification', n:'n = 44 events' },
    delta:'Δ 0.4×', deltaColor:null,
    stats:[
      { lbl:'p-value',   val:'0.19', cls:'good' },
      { lbl:"Cohen's d", val:'0.18', cls:null },
      { lbl:'Note',      val:'n < 30 each side', cls:'warn' },
    ],
    finding: { lbl:'Plain English Finding', text:"Removing a keystone from an ecosystem amplifies disruption by approximately 3× the keystone's proportional size. Removing a keystone institution from a human community amplifies disruption by approximately 2.8×. Not statistically distinguishable — but medium confidence only. Needs more data in the human dataset to upgrade." },
    caveat: 'Medium confidence — fewer than 30 events per side. Run 4 will add Crunchbase founding team data and SEC market maker data. Confidence expected to upgrade to HIGH.',
  },
  {
    num:'03', name:'Refugia Distribution', verdict:'NEAR-PARALLEL', conf:'MEDIUM confidence',
    eco: { vals:[4,8,14,18,16,12,10,8,5,2,1], mean:'0.31', lbl:'min refugia distribution score', n:'n = 44 events' },
    hum: { vals:[2,5,9,14,18,20,16,10,4,2,0], mean:'0.40', lbl:'min refugia distribution score', n:'n = 85 events' },
    delta:'Δ 0.09', deltaColor:null,
    stats:[
      { lbl:'p-value',         val:'0.03',                          cls:'warn' },
      { lbl:"Cohen's d",       val:'0.21',                          cls:null },
      { lbl:'Interpretation',  val:'Significant but small effect',  cls:null },
    ],
    finding: { lbl:'Plain English Finding', text:'Ecological systems require refugia distributed across at least 31% of the original range to maintain recovery potential. Human cultural and knowledge systems show a slightly higher threshold at 40%. Statistically different but practically small — human systems may require somewhat wider geographic distribution to survive catastrophe than biological systems. The direction of the effect is consistent with intentional design — human institutions can strategically place refugia, which may explain why the threshold is higher.' },
    caveat: null,
  },
  {
    num:'04', name:'Behavioral Flexibility Index', verdict:'PARALLEL', conf:'HIGH confidence',
    eco: { vals:[1,2,5,10,16,22,20,14,7,2,1], mean:'0.67', lbl:'flexibility survival correlation', n:'n = 63 events' },
    hum: { vals:[1,3,6,12,18,21,18,12,6,2,1], mean:'0.63', lbl:'flexibility survival correlation', n:'n = 56 events' },
    delta:'Δ 0.04', deltaColor:null,
    stats:[
      { lbl:'p-value',   val:'0.41', cls:'good' },
      { lbl:"Cohen's d", val:'0.09', cls:null },
    ],
    finding: { lbl:'Plain English Finding', text:'Behavioral flexibility predicts survival through disruption at nearly identical rates in biological and human systems. A species with high diet breadth and habitat flexibility survives environmental shifts at the same rate as an organization with diversified business models survives market disruptions. The Crow strategy outperforms the Panda strategy by the same margin — across all living systems.' },
    caveat: null,
  },
  {
    num:'05', name:'Recovery Asymmetry Ratio', verdict:'DIVERGENT', conf:'HIGH confidence',
    eco: { vals:[1,2,4,8,14,20,22,16,8,4,1], mean:'8.4×', lbl:'collapse faster than recovery', n:'n = 124 events', meanColor:C.sage },
    hum: { vals:[3,8,16,22,20,14,8,4,2,2,1], mean:'4.3×', lbl:'collapse faster than recovery', n:'n = 38 events',  meanColor:C.terra, color:C.terra },
    delta:'Δ 4.1×', deltaColor:C.terra,
    stats:[
      { lbl:'p-value',   val:'0.002',         cls:'terra' },
      { lbl:"Cohen's d", val:'0.58',          cls:'terra' },
      { lbl:'Effect',    val:'Medium-large',  cls:'terra' },
    ],
    finding: {
      lbl: 'Plain English Finding — Most Interesting Result',
      paragraphs: [
        'Ecological systems collapse 8.4× faster than they recover. Human systems collapse 4.3× faster than they recover. Both are asymmetric — collapse is always faster than recovery in living systems. But human systems recover twice as fast as ecological systems relative to the speed of their collapse.',
        'The hypothesis: intentional intervention. Humans can recognize a collapse and respond deliberately. Ecosystems cannot. The difference in asymmetry may be the measurable signature of conscious agency — the ability to decide to recover and act on that decision.',
      ],
    },
    caveat: 'This divergence is the most important finding of Run 3. It warrants deeper investigation — specifically whether the human recovery advantage holds across crisis types, or whether certain human system collapses (e.g. civilizational) mirror ecological asymmetry more closely.',
  },
];


const SHIFT_NOTES = [
  {
    color: C.purple, label: '◈ After Run 3',
    body: 'The Recovery Asymmetry divergence is the most publishable finding. 4 of 5 dynamics are parallel — that confirms the hypothesis. But the divergence on Dynamic 5 is where the real insight lives. The 8.4× vs 4.3× gap is the measurable signature of conscious agency — and that framing alone could carry a Nature Sustainability submission.',
  },
  {
    color: C.gold, label: '◈ Blind Spot',
    body: "The IMF financial crisis dataset has only 38 events — the smallest in the simulation. Adding Tainter's civilizational collapse dataset (27 events) and the Diamond dataset (19 events) would bring the human Recovery Asymmetry sample to 84 events and upgrade confidence from HIGH to VERY HIGH for the most important finding.",
  },
];

const RUN_HISTORY = [
  { date:'Apr 25 · Run 3', events:'499 events', badge:'HIGH', current:true },
  { date:'Apr 12 · Run 2', events:'312 events', badge:'MED' },
  { date:'Apr 3 · Run 1',  events:'187 events', badge:'LOW' },
];

const PAPER_READINESS = {
  pct: 42,
  items: [
    { state:'done',     label:'Hypothesis defined' },
    { state:'done',     label:'Ecological dataset complete' },
    { state:'progress', label:'Human datasets (5 of 7 loaded)' },
    { state:'progress', label:'3 dynamics at HIGH confidence' },
    { state:'pending',  label:'All 5 at HIGH confidence' },
    { state:'pending',  label:'Draft introduction written' },
    { state:'pending',  label:'Submit to PLOS ONE' },
  ],
};

// ─── Component ───────────────────────────────────────────
export default function ContinuumSimulation({ workspaceId }) {
  const [screen, setScreen] = useState('findings');  // 'setup' | 'findings' | 'behaviors' | 'scenarios'
  const [running, setRunning] = useState(false);
  const [codeTab, setCodeTab] = useState('extract');

  // Live state — fetched from /api/continuum/comparisons (latest run)
  const [run, setRun] = useState(null);
  const [comparisons, setComparisons] = useState(null);

  // Engine B Phase 1 — behavior discovery state
  const [discovering, setDiscovering] = useState(false);
  const [discoveryError, setDiscoveryError] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    const r = await fetch(`/api/continuum/comparisons?workspace_id=${workspaceId}`);
    if (r.ok) {
      const j = await r.json();
      setRun(j.run);
      setComparisons(j.comparisons || []);
    } else {
      setComparisons([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  async function runSimulation() {
    if (running) return;
    setRunning(true);
    try {
      const res = await fetch('/api/continuum/run-simulation', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, trigger_reason: 'manual_button' }),
      });
      if (res.ok) {
        const j = await res.json();
        setRun(j.run);
        setComparisons(j.comparisons || []);
        setScreen('findings');
      }
    } catch { /* swallow — error visible via empty findings */ }
    setRunning(false);
  }

  async function discoverBehaviorsNow() {
    if (discovering || !run?.id) return;
    setDiscovering(true);
    setDiscoveryError(null);
    try {
      const res = await fetch('/api/continuum/discover-patterns', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, run_id: run.id }),
      });
      const j = await res.json();
      if (!res.ok) {
        setDiscoveryError(j.error || `Status ${res.status}`);
      } else {
        // CRITICAL: even on a 200 response, the persist step is best-
        // effort. If persist failed, the local state shows the report
        // but a refresh wipes it (no DB row to load from). Surface
        // that loudly so the user knows to act.
        if (j.persist_error) {
          setDiscoveryError(
            `Discovery succeeded but persist failed: ${j.persist_error}` +
            (j.persist_hint ? `\n\n${j.persist_hint}` : '')
          );
        }
        // Merge the discovered behaviors into the local run object so the
        // UI updates immediately. (Even if persist failed, user sees
        // the result this session — they just lose it on refresh.)
        setRun(prev => prev ? {
          ...prev,
          behaviors:          j.discovered,
          behaviors_at:       j.discovered_at,
          behaviors_model:    j.model,
          behaviors_sample_n: j.sample_size,
        } : prev);
        // Verify by re-fetching the run from the DB. If behaviors are
        // present, persist worked. If absent, we have proof the
        // persist failed and can show a clearer diagnostic.
        if (!j.persist_error) {
          // Defer slightly so the UI updates with the local result first,
          // then we verify in the background.
          setTimeout(() => { reload(); }, 1500);
        }
      }
    } catch (err) {
      setDiscoveryError(err.message);
    }
    setDiscovering(false);
  }

  return (
    <div style={{
      height:'100%', background:C.bg, color:C.text,
      fontFamily:F.mono, fontSize:12,
      display:'flex', flexDirection:'column', overflow:'hidden',
    }}>
      {/* ── Mini topbar (run badge + run button) ──────────── */}
      <div style={{
        display:'flex', alignItems:'center',
        padding:'0 20px', height:42,
        borderBottom:`1px solid ${C.b1}`,
        background:C.s1, flexShrink:0, gap:14,
      }}>
        <div style={{
          display:'flex', alignItems:'center', gap:6,
          fontSize:9, padding:'3px 10px', borderRadius:4,
          background:'rgba(90,158,181,.1)',
          border:'1px solid rgba(90,158,181,.18)',
          color:C.teal,
        }}>
          <div style={{
            width:5, height:5, borderRadius:'50%', background:C.teal,
            animation:'continuumPulse 2s infinite',
          }} />
          {run
            ? `Run ${run.id?.slice(0, 6)} · ${run.completed_at ? new Date(run.completed_at).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' }) : 'in progress'} · ${run.comparison_count || 0} comparisons`
            : 'No runs yet — click ▶ Run Simulation'}
        </div>

        {/* Inner switcher */}
        <div style={{ display:'flex', alignItems:'center', gap:5, marginLeft:14 }}>
          <span style={{ fontSize:8, color:C.dim, letterSpacing:'0.1em', marginRight:2 }}>VIEW:</span>
          {[
            { id:'setup',     label:'Simulation Setup' },
            { id:'findings',  label:'Findings Dashboard' },
            { id:'behaviors', label:'Behaviors (Open)' },
            { id:'scenarios', label:'What-If Scenarios' },
          ].map(s => {
            const on = screen === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setScreen(s.id)}
                style={{
                  fontSize:9, padding:'3px 11px', borderRadius:3,
                  border:`1px solid ${on ? 'rgba(200,169,110,.2)' : C.b2}`,
                  background: on ? 'rgba(200,169,110,.1)' : 'none',
                  color: on ? C.gold : C.static,
                  cursor:'pointer', fontFamily:F.mono, transition:'all .1s',
                }}
                onMouseEnter={e => { if (!on) e.currentTarget.style.color = C.mid; }}
                onMouseLeave={e => { if (!on) e.currentTarget.style.color = C.static; }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ fontSize:9, color:C.dim }}>
            {run?.completed_at
              ? `Last run: ${new Date(run.completed_at).toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}`
              : 'No runs yet'}
          </div>
          <button
            onClick={runSimulation}
            disabled={running}
            style={{
              fontSize:9, padding:'5px 16px', borderRadius:5,
              background: running ? 'rgba(90,158,181,.12)' : 'rgba(200,169,110,.12)',
              border: `1px solid ${running ? 'rgba(90,158,181,.25)' : 'rgba(200,169,110,.25)'}`,
              color: running ? C.teal : C.gold,
              cursor: running ? 'not-allowed' : 'pointer',
              fontFamily:F.mono, transition:'all .15s', letterSpacing:'0.06em',
            }}
            onMouseEnter={e => { if (!running) e.currentTarget.style.background = 'rgba(200,169,110,.22)'; }}
            onMouseLeave={e => { if (!running) e.currentTarget.style.background = 'rgba(200,169,110,.12)'; }}
          >
            {running ? '⟳ Running…' : '▶ Run Simulation'}
          </button>
        </div>
      </div>

      {/* Inline keyframes */}
      <style>{`@keyframes continuumPulse {0%,100%{opacity:1;}50%{opacity:.3;}}`}</style>

      {/* ── Screen content ─────────────────────────────────── */}
      <div style={{ flex:1, minHeight:0, overflow:'hidden' }}>
        {screen === 'setup'     && <SetupScreen codeTab={codeTab} setCodeTab={setCodeTab} />}
        {screen === 'findings'  && <FindingsScreen run={run} comparisons={comparisons} running={running} />}
        {screen === 'behaviors' && (
          <BehaviorsScreen
            run={run}
            comparisons={comparisons}
            discovering={discovering}
            discoveryError={discoveryError}
            onDiscover={discoverBehaviorsNow}
          />
        )}
        {screen === 'scenarios' && <ScenariosScreen workspaceId={workspaceId} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SETUP SCREEN
// ═══════════════════════════════════════════════════════════
function SetupScreen({ codeTab, setCodeTab }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'260px 1fr 280px', height:'100%', overflow:'hidden' }}>
      {/* Pipeline column */}
      <div style={{ borderRight:`1px solid ${C.b1}`, display:'flex', flexDirection:'column', overflow:'hidden', background:C.s1 }}>
        <ColHeader>Simulation Pipeline<ColAction>View log</ColAction></ColHeader>
        <div style={{ flex:1, overflowY:'auto', padding:8 }}>
          {PIPELINE.map((s, i) => <PipelineStep key={i} step={s} />)}
        </div>
      </div>

      {/* Code column */}
      <div style={{ display:'flex', flexDirection:'column', overflow:'hidden', background:C.bg }}>
        <div style={{ display:'flex', borderBottom:`1px solid ${C.b1}`, flexShrink:0, background:C.s1 }}>
          {CODE_TABS.map(t => {
            const on = codeTab === t.id;
            return (
              <div
                key={t.id}
                onClick={() => setCodeTab(t.id)}
                style={{
                  fontSize:9, padding:'8px 16px',
                  color: on ? C.teal : C.static,
                  cursor:'pointer',
                  borderBottom:`2px solid ${on ? C.teal : 'transparent'}`,
                  transition:'all .12s',
                }}
                onMouseEnter={e => { if (!on) e.currentTarget.style.color = C.mid; }}
                onMouseLeave={e => { if (!on) e.currentTarget.style.color = C.static; }}
              >
                {t.label}
              </div>
            );
          })}
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:16 }}>
          {codeTab === 'what'
            ? <HowItWorks />
            : <CodePanel data={CODE_FILES[codeTab]} />}
        </div>
      </div>

      {/* Datasets column */}
      <div style={{ borderLeft:`1px solid ${C.b1}`, display:'flex', flexDirection:'column', overflow:'hidden', background:C.s1 }}>
        <ColHeader>Datasets Loaded<ColAction>+ Add dataset</ColAction></ColHeader>
        <div style={{ flex:1, overflowY:'auto', padding:8 }}>
          {DATASETS.map((g, i) => (
            <div key={i}>
              <div style={{
                fontFamily:F.mono, fontSize:7, letterSpacing:'0.18em', textTransform:'uppercase',
                color:C.dim, padding:'10px 6px 6px',
              }}>{g.group}</div>
              {g.items.map((d, j) => <DatasetItem key={j} d={d} />)}
            </div>
          ))}
          <div style={{ height:20 }} />
        </div>
      </div>
    </div>
  );
}

function ColHeader({ children }) {
  return (
    <div style={{
      padding:'10px 14px', borderBottom:`1px solid ${C.b1}`,
      background:C.s2, flexShrink:0,
      fontSize:7, letterSpacing:'0.18em', textTransform:'uppercase',
      color:C.dim,
      display:'flex', alignItems:'center', justifyContent:'space-between',
    }}>
      {children}
    </div>
  );
}

function ColAction({ children }) {
  return (
    <span style={{
      fontSize:8, color:C.teal, cursor:'pointer',
      letterSpacing:'0.06em', textTransform:'none',
    }}>
      {children}
    </span>
  );
}

function PipelineStep({ step }) {
  const numCol = step.state === 'done'
    ? { bg:'rgba(122,158,130,.2)', fg:C.sage,  br:'rgba(122,158,130,.3)' }
    : step.state === 'active'
    ? { bg:'rgba(200,169,110,.15)', fg:C.gold, br:'rgba(200,169,110,.3)' }
    : { bg:C.b2, fg:C.dim, br:C.b3 };
  const labelCol = step.state === 'done' ? C.sage : step.state === 'active' ? C.gold : C.dim;
  const nameCol  = step.state === 'done' ? C.static : step.state === 'active' ? C.mid : C.dim;
  const statusBg = step.state === 'done'
    ? 'rgba(122,158,130,.1)'
    : step.state === 'active'
    ? 'rgba(200,169,110,.1)'
    : C.b2;
  const statusFg = step.state === 'done' ? C.sage : step.state === 'active' ? C.gold : C.dim;
  const statusLabel = step.state === 'done' ? 'Done' : step.state === 'active' ? 'Running' : 'Pending';

  return (
    <div style={{
      background:C.s2, border:`1px solid ${C.b2}`, borderRadius:8,
      marginBottom:6, overflow:'hidden',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px' }}>
        <div style={{
          width:20, height:20, borderRadius:'50%',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:9, fontWeight:500, flexShrink:0,
          background:numCol.bg, color:numCol.fg,
          border:`1px solid ${numCol.br}`,
        }}>
          {step.num}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:7, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:2, color:labelCol }}>
            {step.label}
          </div>
          <div style={{ fontSize:11, fontFamily:F.sans, color:nameCol }}>
            {step.name}
          </div>
        </div>
        <div style={{ fontSize:7, padding:'2px 7px', borderRadius:3, flexShrink:0, background:statusBg, color:statusFg }}>
          {statusLabel}
        </div>
      </div>
      <div style={{
        padding:'0 12px 10px 40px', fontSize:10, color:C.dim,
        lineHeight:1.6, fontFamily:F.sans,
      }}>
        {step.detail}
        {step.metric && (
          <div style={{
            display:'flex', alignItems:'center', gap:6,
            marginTop:6, padding:'5px 8px',
            background:C.bg, borderRadius:5, border:`1px solid ${C.b1}`,
          }}>
            <div style={{ fontSize:12, color:step.metric.color }}>{step.metric.val}</div>
            <div style={{ fontSize:9, color:C.dim }}>{step.metric.lbl}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function CodePanel({ data }) {
  return (
    <div>
      <div style={{
        background:C.s2, border:`1px solid ${C.b2}`, borderRadius:8,
        padding:'10px 14px', marginBottom:12,
      }}>
        <div style={{ fontFamily:F.mono, fontSize:7, letterSpacing:'0.18em', textTransform:'uppercase', color:C.gold, marginBottom:6 }}>
          What this file does
        </div>
        <div style={{ fontFamily:F.sans, fontSize:12, color:C.mid, lineHeight:1.65 }}>
          {data.narr}
        </div>
      </div>

      <div style={{
        background:C.s1, border:`1px solid ${C.b2}`, borderRadius:8,
        overflow:'hidden', marginBottom:12,
      }}>
        <div style={{
          padding:'7px 14px', background:C.s2,
          borderBottom:`1px solid ${C.b1}`,
          display:'flex', alignItems:'center', gap:8,
        }}>
          <span style={{ fontSize:8, letterSpacing:'0.12em', textTransform:'uppercase', color:C.teal }}>Python</span>
          <span style={{ fontSize:9, color:C.static }}>{data.file}</span>
          <span style={{ marginLeft:'auto', fontSize:8, color:C.static, cursor:'pointer', letterSpacing:'0.06em' }}>Copy</span>
        </div>
        <pre style={{
          margin:0, padding:14, fontFamily:F.mono, fontSize:11, lineHeight:1.55,
          color:C.mid, whiteSpace:'pre', overflowX:'auto',
        }}>
          {colorizePython(data.code)}
        </pre>
      </div>
    </div>
  );
}

// Minimal Python "syntax highlighting" — comments + docstrings only.
// Keeps the visual cue of the source mockup without a full lexer.
function colorizePython(code) {
  const lines = code.split('\n');
  return lines.map((line, i) => {
    const trimmed = line.trimStart();
    let color = C.mid;
    if (trimmed.startsWith('#')) color = C.dim;
    else if (trimmed.startsWith('"""') || trimmed.startsWith("'''")) color = C.sage;
    return <div key={i} style={{ color }}>{line || '\u00a0'}</div>;
  });
}

function HowItWorks() {
  return (
    <>
      {HOW_IT_WORKS.map((block, bi) => (
        <div key={bi} style={{
          background:C.s2, border:`1px solid ${C.b2}`, borderRadius:8,
          padding:'12px 14px', marginBottom:12,
        }}>
          <div style={{ fontFamily:F.mono, fontSize:7, letterSpacing:'0.18em', textTransform:'uppercase', color:C.gold, marginBottom:8 }}>
            {block.eyebrow}
          </div>
          <div style={{ fontFamily:F.sans, fontSize:12, color:C.mid, lineHeight:1.65 }}>
            {block.body.map((b, i) => {
              if (b.kind === 'p') {
                return (
                  <p key={i} style={{ marginBottom:10 }}>
                    {b.strong && <strong style={{ color:C.text, fontWeight:500 }}>{b.strong}</strong>}
                    {b.text}
                  </p>
                );
              }
              if (b.kind === 'rule') {
                const hlColor = b.cls === 'parallel' ? C.sage : b.cls === 'near' ? C.gold : C.terra;
                return (
                  <p key={i} style={{ marginBottom:10 }}>
                    <span style={{ color:hlColor, fontWeight:500 }}>{b.hl}</span>
                    {b.text}
                  </p>
                );
              }
              return null;
            })}
          </div>
        </div>
      ))}
    </>
  );
}

function DatasetItem({ d }) {
  const dotColor = d.state === 'live' ? C.sage : d.state === 'partial' ? C.gold : C.dim;
  const nColor   = d.state === 'live' ? C.sage : d.state === 'partial' ? C.gold : C.dim;
  return (
    <div style={{
      background:C.s2, border:`1px solid ${C.b2}`, borderRadius:6,
      padding:'8px 10px', marginBottom:6,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
        <div style={{ width:6, height:6, borderRadius:'50%', background:dotColor, flexShrink:0 }} />
        <div style={{ flex:1, fontSize:11, fontFamily:F.sans, color:C.mid }}>{d.name}</div>
        <div style={{ fontSize:11, fontFamily:F.mono, color:nColor }}>{d.n}</div>
      </div>
      <div style={{ fontSize:10, color:C.dim, lineHeight:1.5, fontFamily:F.sans, marginBottom:6 }}>
        {d.desc}
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
        {d.dyns.map((id, i) => {
          const b = DYN_BADGES[id];
          if (!b) return null;
          return (
            <span key={i} style={{
              fontSize:8, padding:'1px 6px', borderRadius:3,
              background:`${b.color}15`, border:`1px solid ${b.color}30`,
              color:b.color, letterSpacing:'0.04em',
            }}>
              {b.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// FINDINGS SCREEN — reads real continuum_runs + continuum_comparisons.
// When no run exists yet, shows an empty state. While running, shows a
// loading state. Otherwise: real metadata strip + per-dynamic cards.
// ═══════════════════════════════════════════════════════════
function FindingsScreen({ run, comparisons, running }) {
  // Loading state: comparisons === null means we're still fetching
  if (comparisons === null) {
    return (
      <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:C.dim, fontSize:12, fontStyle:'italic' }}>
        Loading…
      </div>
    );
  }
  // No run yet
  if (!run) {
    return (
      <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', padding:32 }}>
        <div style={{ maxWidth:520, textAlign:'center' }}>
          <div style={{ fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:C.gold, marginBottom:14 }}>
            No simulation run yet
          </div>
          <div style={{ fontFamily:F.serif, fontSize:24, color:C.text, marginBottom:12, fontWeight:300 }}>
            Click ▶ Run Simulation to compute findings
          </div>
          <div style={{ fontSize:12, color:C.mid, lineHeight:1.7 }}>
            The engine will pull all loaded eco + human events, run Welch&apos;s t-test for each dynamic that has enough data, and persist the comparisons. Dynamics without enough data render as &quot;pending&quot; — honest, not faked.
          </div>
        </div>
      </div>
    );
  }

  // Compute summary counts from real comparisons
  const counts = comparisons.reduce((acc, c) => {
    if      (c.verdict === 'PARALLEL')          acc.parallel++;
    else if (c.verdict === 'NEAR-PARALLEL')     acc.near++;
    else if (c.verdict === 'DIVERGENT')         acc.divergent++;
    else                                         acc.pending++;
    return acc;
  }, { parallel: 0, near: 0, divergent: 0, pending: 0 });

  const totalEvents = Object.values(run.data_snapshot || {}).reduce(
    (sum, src) => sum + (src.eco_event_count || 0) + (src.human_event_count || 0),
    0
  );

  const runMeta = [
    { lbl: 'Simulation Run',  val: run.id?.slice(0, 6) || '—',          color: C.gold },
    { lbl: 'Total Events',    val: totalEvents.toLocaleString(),          color: C.teal },
    { lbl: 'Parallel',        val: String(counts.parallel),               color: C.sage },
    { lbl: 'Near-Parallel',   val: String(counts.near),                   color: C.gold },
    { lbl: 'Divergent',       val: String(counts.divergent),              color: C.terra },
    { lbl: 'Pending',         val: String(counts.pending),                color: C.dim },
  ];

  // Map comparisons by dynamic_key for canonical-order rendering
  const byDynamic = Object.fromEntries(comparisons.map(c => [c.dynamic_key, c]));

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', height:'100%', overflow:'hidden' }}>
      <div style={{ overflowY:'auto', padding:'18px 22px' }}>
        {/* Run metadata strip */}
        <div style={{
          display:'flex', alignItems:'stretch', gap:0,
          background:C.s1, border:`1px solid ${C.b1}`, borderRadius:8,
          padding:'12px 16px', marginBottom:14, flexWrap:'wrap',
        }}>
          {runMeta.map((m, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div>
                <div style={{ fontSize:8, color:C.dim, letterSpacing:'0.14em', textTransform:'uppercase', marginBottom:2 }}>
                  {m.lbl}
                </div>
                <div style={{ fontSize:18, color:m.color, fontWeight:500 }}>{m.val}</div>
              </div>
              {i < runMeta.length - 1 && (
                <div style={{ width:1, height:28, background:C.b2, margin:'0 18px' }} />
              )}
            </div>
          ))}
          <div style={{ marginLeft:'auto', alignSelf:'center', fontSize:9, color:C.dim }}>
            {running
              ? '⟳ Running…'
              : `Run complete · ${run.completed_at ? new Date(run.completed_at).toLocaleString(undefined, { month:'short', day:'numeric', year:'numeric' }) : ''}`}
          </div>
        </div>

        {DYNAMICS.map((dyn, i) => {
          const comp = byDynamic[dyn.key];
          const f = adaptComparisonToCard(comp, dyn, i + 1);
          return <FindingCard key={dyn.key} f={f} />;
        })}
      </div>

      <FindingsSidebar comparisons={comparisons} />
    </div>
  );
}

// Compact "mean ± std · med X" formatter. Median exposed inline lets
// reviewers spot heavy-tail distributions without drilling into raw
// data — when median << mean, the mean is being pulled by outliers.
function formatMeanWithMedian(mean, std, median) {
  if (mean == null) return '—';
  const meanStr = Number(mean).toFixed(3);
  const stdStr  = std != null  ? `${Number(std).toFixed(3)}` : '?';
  const medStr  = median != null ? ` · med ${Number(median).toFixed(3)}` : '';
  return `${meanStr} ± ${stdStr}${medStr}`;
}

// Map a comparison row + dynamic metadata → the existing FindingCard
// shape. Preserves the visual styling we already shipped.
function adaptComparisonToCard(comp, dyn, num) {
  const verdict = comp?.verdict || 'pending';
  const numStr = String(num).padStart(2, '0');

  const ecoEvents   = comp?.data_inputs?.eco?.events   || [];
  const humanEvents = comp?.data_inputs?.human?.events || [];
  const ecoTruncated   = !!comp?.data_inputs?.eco?.events_truncated;
  const humanTruncated = !!comp?.data_inputs?.human?.events_truncated;

  // Pending / no comparison yet
  if (!comp || verdict === 'pending' || verdict === 'insufficient_data' || verdict === 'error') {
    return {
      num: numStr,
      name: dyn.label,
      verdict: verdict === 'insufficient_data' ? 'INSUFFICIENT' : verdict === 'error' ? 'ERROR' : 'PENDING',
      conf: '—',
      eco:   { vals: [], mean: '—', lbl: dyn.units, n: comp ? `n = ${comp.eco_n || 0}` : 'n = 0', events: ecoEvents, eventsTruncated: ecoTruncated },
      hum:   { vals: [], mean: '—', lbl: dyn.units, n: comp ? `n = ${comp.human_n || 0}` : 'n = 0', events: humanEvents, eventsTruncated: humanTruncated },
      delta: '',
      stats: [],
      finding: { lbl: 'Plain English Finding', text: comp?.finding_text || `Pending — ${dyn.description}` },
      caveat: null,
      isPending: true,
    };
  }

  const ecoHist   = comp.data_inputs?.eco?.histogram?.bins   || [];
  const humanHist = comp.data_inputs?.human?.histogram?.bins || [];

  const meanA = comp.eco_mean,    meanB = comp.human_mean;
  const delta = (meanA != null && meanB != null) ? Math.abs(meanA - meanB).toFixed(3) : '';
  const deltaColor = verdict === 'DIVERGENT' ? C.terra : null;

  return {
    num: numStr,
    name: dyn.label,
    verdict,
    conf: comp.confidence ? `${comp.confidence} confidence` : '—',
    eco: {
      vals: ecoHist,
      mean: meanA != null ? Number(meanA).toFixed(3) : '—',
      lbl: dyn.units,
      n: `n = ${comp.eco_n || 0}`,
      meanColor: C.sage,
      events: ecoEvents,
      eventsTruncated: ecoTruncated,
    },
    hum: {
      vals: humanHist,
      mean: meanB != null ? Number(meanB).toFixed(3) : '—',
      lbl: dyn.units,
      n: `n = ${comp.human_n || 0}`,
      color: verdict === 'DIVERGENT' ? C.terra : C.teal,
      meanColor: verdict === 'DIVERGENT' ? C.terra : C.teal,
      events: humanEvents,
      eventsTruncated: humanTruncated,
    },
    delta: delta ? `Δ ${delta}` : '',
    deltaColor,
    stats: [
      { lbl: 'p-value',  val: comp.p_value != null ? String(comp.p_value) : '—', cls: comp.p_value > 0.05 ? 'good' : verdict === 'DIVERGENT' ? 'terra' : 'warn' },
      { lbl: "Cohen's d", val: comp.effect_size != null ? String(comp.effect_size) : '—', cls: verdict === 'DIVERGENT' ? 'terra' : null },
      { lbl: 'eco mean',   val: formatMeanWithMedian(meanA, comp.eco_std,   comp.data_inputs?.eco?.median),   cls: null },
      { lbl: 'human mean', val: formatMeanWithMedian(meanB, comp.human_std, comp.data_inputs?.human?.median), cls: null },
    ],
    finding: {
      lbl: verdict === 'DIVERGENT' ? 'Plain English Finding — Divergent Result' : 'Plain English Finding',
      text: comp.finding_text || '',
    },
    caveat: comp.caveat,
    secondary: comp?.data_inputs?.secondary || null,
  };
}

// ═══════════════════════════════════════════════════════════
// BEHAVIORS SCREEN — Engine B Phase 1
// Hypothesis-free pattern discovery. Sends a stratified sample of
// events to Claude with NO predefined dynamics, asks for behavioral
// patterns, renders the structured output as cards.
// ═══════════════════════════════════════════════════════════
function BehaviorsScreen({ run, comparisons, discovering, discoveryError, onDiscover }) {
  const behaviors = run?.behaviors;
  const hasEventsInRun = (comparisons || []).some(c =>
    (c.data_inputs?.eco?.events?.length || 0) + (c.data_inputs?.human?.events?.length || 0) > 0
  );

  if (!run) {
    return <BehaviorsEmptyState message="No simulation run yet — go to Findings Dashboard and click ▶ Run Simulation first." />;
  }
  if (!hasEventsInRun) {
    return <BehaviorsEmptyState message="This run has no event-level data. Run a fresh simulation to capture events, then come back." />;
  }

  return (
    <div style={{ height:'100%', overflowY:'auto', padding:'18px 22px' }}>
      {/* Header strip — explanation + discover button */}
      <div style={{
        display:'flex', alignItems:'flex-start', gap:18, marginBottom:18,
        padding:'14px 18px', background:C.s1, border:`1px solid ${C.b1}`, borderRadius:8,
      }}>
        <div style={{ flex:1 }}>
          <div style={{
            fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase',
            color:C.purple, marginBottom:6, fontWeight:500,
          }}>
            Engine B · Open / Hypothesis-free
          </div>
          <div style={{ fontFamily:F.serif, fontSize:18, color:C.text, marginBottom:6, fontWeight:300 }}>
            What patterns does the data show — without our predefined dynamics?
          </div>
          <div style={{ fontSize:11, color:C.mid, lineHeight:1.6, maxWidth:720 }}>
            A sample of this run&apos;s events is sent to Claude with NO theoretical framework. Claude is asked to identify behavioral patterns it sees in the crash-and-recovery data, name them, and flag whether each pattern matches one of our 5 predefined dynamics or is something novel. The output is independent evidence for or against the Continuum hypothesis frame.
          </div>
          {behaviors?.meta?.summary && (
            <div style={{
              marginTop:10, padding:'8px 12px',
              background:'rgba(155,143,196,.06)', border:'1px solid rgba(155,143,196,.18)',
              borderRadius:6, fontSize:12, color:C.text, lineHeight:1.6, fontStyle:'italic',
            }}>
              &ldquo;{behaviors.meta.summary}&rdquo;
            </div>
          )}
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
          <button
            onClick={onDiscover}
            disabled={discovering}
            style={{
              fontSize:9, padding:'6px 18px', borderRadius:5,
              background: discovering ? 'rgba(155,143,196,.12)' : 'rgba(155,143,196,.18)',
              border: `1px solid ${discovering ? 'rgba(155,143,196,.25)' : 'rgba(155,143,196,.35)'}`,
              color: C.purple,
              cursor: discovering ? 'not-allowed' : 'pointer',
              fontFamily:F.mono, transition:'all .15s', letterSpacing:'0.06em',
              whiteSpace:'nowrap',
            }}
          >
            {discovering ? '⟳ Asking Claude…' : behaviors ? '↻ Re-discover' : '▶ Discover Behaviors'}
          </button>
          {run?.behaviors_at && (
            <div style={{ fontSize:9, color:C.dim }}>
              Last: {new Date(run.behaviors_at).toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
            </div>
          )}
          {run?.behaviors_sample_n && (
            <div style={{ fontSize:9, color:C.dim }}>
              Sample: {run.behaviors_sample_n} events
            </div>
          )}
        </div>
      </div>

      {discoveryError && (
        <div style={{
          padding:'12px 14px', marginBottom:14,
          background:'rgba(196,80,80,.06)', border:'1px solid rgba(196,80,80,.3)',
          borderRadius:6, color:C.terra, fontSize:11, lineHeight:1.5,
        }}>
          Discovery failed: {discoveryError}
        </div>
      )}

      {/* Behaviors */}
      {behaviors?._parse_error && (
        <div style={{
          padding:'12px 14px', marginBottom:14,
          background:'rgba(212,149,106,.06)', border:'1px solid rgba(212,149,106,.3)',
          borderRadius:6, color:C.amber, fontSize:11, lineHeight:1.5,
        }}>
          Claude responded but the output couldn&apos;t be parsed as JSON. Try ↻ Re-discover. Raw preview:
          <pre style={{ marginTop:8, fontSize:10, color:C.dim, whiteSpace:'pre-wrap' }}>{behaviors.raw}</pre>
        </div>
      )}

      {behaviors?.behaviors?.length > 0 && (
        <div style={{ marginBottom:18 }}>
          <SectionHeader label={`${behaviors.behaviors.length} behaviors discovered`} color={C.purple} />
          <div style={{
            display:'grid',
            // 2-up at wide viewports, auto-collapse to 1-up below ~1180px
            gridTemplateColumns:'repeat(auto-fit, minmax(560px, 1fr))',
            gap:12,
          }}>
            {behaviors.behaviors.map((b, i) => <BehaviorCard key={i} b={b} num={i + 1} />)}
          </div>
        </div>
      )}

      {behaviors?.anomalies?.length > 0 && (
        <div style={{ marginBottom:18 }}>
          <SectionHeader label={`${behaviors.anomalies.length} anomalies — events that fit no pattern`} color={C.amber} />
          <div style={{
            display:'grid',
            gridTemplateColumns:'repeat(auto-fit, minmax(420px, 1fr))',
            gap:10,
          }}>
            {behaviors.anomalies.map((a, i) => <AnomalyCard key={i} a={a} />)}
          </div>
        </div>
      )}

      {!behaviors && !discovering && (
        <div style={{
          textAlign:'center', padding:'40px 20px',
          fontSize:12, color:C.mid, lineHeight:1.7,
        }}>
          No behaviors discovered yet for this run.<br/>
          Click <span style={{ color:C.purple }}>▶ Discover Behaviors</span> to ask Claude.
        </div>
      )}
    </div>
  );
}

function BehaviorsEmptyState({ message }) {
  return (
    <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', padding:32 }}>
      <div style={{ maxWidth:520, textAlign:'center' }}>
        <div style={{ fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:C.purple, marginBottom:14 }}>
          Engine B — Behaviors (Open)
        </div>
        <div style={{ fontFamily:F.serif, fontSize:20, color:C.text, marginBottom:12, fontWeight:300 }}>
          {message}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ label, color }) {
  return (
    <div style={{
      fontFamily:F.mono, fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase',
      color, marginBottom:10, fontWeight:500,
    }}>
      ◇ {label}
    </div>
  );
}

const TRANSFER_COLOR = {
  both:        C.sage,
  eco_only:    C.teal,
  human_only:  C.gold,
  unclear:     C.dim,
};
const CONFIDENCE_COLOR = {
  strong:      C.sage,
  recurring:   C.gold,
  provisional: C.amber,
};

function BehaviorCard({ b, num }) {
  const [expanded, setExpanded] = useState(false);
  const transferColor = TRANSFER_COLOR[b.transferability] || C.dim;
  const confColor     = CONFIDENCE_COLOR[b.confidence]    || C.dim;
  const isNovel = b.relates_to_dynamic === 'novel';
  const evidence = b.evidence_events || [];

  return (
    <div style={{
      background: isNovel ? 'rgba(155,143,196,.04)' : C.s1,
      border: `1px solid ${isNovel ? 'rgba(155,143,196,.25)' : C.b2}`,
      borderRadius:8, overflow:'hidden',
    }}>
      <div style={{
        display:'flex', alignItems:'center', gap:14,
        padding:'12px 18px', background:C.s2, borderBottom:`1px solid ${C.b1}`,
      }}>
        <div style={{ fontFamily:F.mono, fontSize:11, color:C.dim }}>{String(num).padStart(2,'0')}</div>
        <div style={{ fontFamily:F.serif, fontSize:16, fontWeight:300, color:C.text, flex:1 }}>
          {b.name || 'Unnamed pattern'}
        </div>
        {isNovel && (
          <div style={{
            fontSize:8, padding:'2px 8px', borderRadius:3,
            background:'rgba(155,143,196,.15)', border:'1px solid rgba(155,143,196,.4)',
            color:C.purple, letterSpacing:'0.1em',
          }}>
            NOVEL
          </div>
        )}
        <div style={{
          fontSize:9, padding:'2px 8px', borderRadius:3,
          background:`${transferColor}15`, border:`1px solid ${transferColor}50`,
          color:transferColor, letterSpacing:'0.06em', textTransform:'uppercase',
        }}>
          {b.transferability || 'unclear'}
        </div>
        <div style={{
          fontSize:9, padding:'2px 8px', borderRadius:3,
          background:`${confColor}15`, border:`1px solid ${confColor}50`,
          color:confColor, letterSpacing:'0.06em', textTransform:'uppercase',
        }}>
          {b.confidence || '?'}
        </div>
      </div>

      <div style={{ padding:'14px 18px' }}>
        {b.mechanism_hypothesis && (
          <div style={{
            padding:'10px 14px', background:C.bg, border:`1px solid ${C.b1}`, borderRadius:6,
            marginBottom:10,
          }}>
            <div style={{
              fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
              color:C.gold, marginBottom:6, fontWeight:500,
            }}>
              Mechanism Hypothesis
            </div>
            <div style={{ fontFamily:F.sans, fontSize:12, color:C.mid, lineHeight:1.65 }}>
              {b.mechanism_hypothesis}
            </div>
          </div>
        )}

        {b.notes && (
          <div style={{
            padding:'8px 12px', background:'rgba(85,85,102,.05)', border:`1px solid ${C.b1}`,
            borderRadius:5, fontSize:11, color:C.dim, lineHeight:1.5, marginBottom:10,
            fontStyle:'italic',
          }}>
            {b.notes}
          </div>
        )}

        <div style={{
          display:'flex', flexWrap:'wrap', gap:14, marginBottom:8,
          fontFamily:F.mono, fontSize:10, color:C.mid,
        }}>
          <div>relates to: <span style={{ color: isNovel ? C.purple : C.teal }}>{b.relates_to_dynamic || '—'}</span></div>
          <div>evidence: <span style={{ color:C.text }}>{evidence.length} events</span></div>
        </div>

        {evidence.length > 0 && (
          <button
            onClick={() => setExpanded(v => !v)}
            style={{
              background:'transparent', border:`1px solid ${C.b2}`, borderRadius:5,
              padding:'5px 11px', cursor:'pointer',
              fontFamily:F.mono, fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase',
              color:C.static,
            }}
          >
            {expanded ? '▾ Hide evidence' : `▸ Show ${evidence.length} evidence events`}
          </button>
        )}

        {expanded && evidence.length > 0 && (
          <div style={{
            marginTop:10,
            background:C.bg, border:`1px solid ${C.b1}`, borderRadius:5, overflow:'hidden',
          }}>
            {evidence.map((e, i) => (
              <div key={i} style={{
                display:'grid', gridTemplateColumns:'2fr 0.8fr 0.7fr 0.7fr 0.7fr 0.6fr',
                padding:'6px 12px',
                borderBottom: i < evidence.length - 1 ? `1px solid ${C.b3 || C.b2}` : 'none',
                fontFamily:F.mono, fontSize:10, color:C.mid, alignItems:'center',
              }}>
                <div style={{ color:C.text, fontFamily:F.sans, fontSize:11, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {e.system_name || '—'}
                </div>
                <div style={{ color:C.dim, fontSize:9 }}>{e.source_key || '—'}</div>
                <div style={{ textAlign:'right' }}>peak {fmt(e.peak_value)}</div>
                <div style={{ textAlign:'right' }}>trough {fmt(e.trough_value)}</div>
                <div style={{ textAlign:'right' }}>cur {fmt(e.current_value)}</div>
                <div style={{ textAlign:'right', color:C.gold }}>{e.value != null ? Number(e.value).toFixed(3) : '—'}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AnomalyCard({ a }) {
  const e = a.event;
  return (
    <div style={{
      background:'rgba(212,149,106,.04)', border:'1px solid rgba(212,149,106,.25)',
      borderRadius:6, padding:'10px 14px',
    }}>
      <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:6 }}>
        <div style={{ fontFamily:F.serif, fontSize:14, color:C.text, fontWeight:300 }}>
          {e?.system_name || '(unknown event)'}
        </div>
        {e?.source_key && <div style={{ fontFamily:F.mono, fontSize:9, color:C.dim }}>{e.source_key}</div>}
      </div>
      <div style={{ fontFamily:F.sans, fontSize:11, color:C.mid, lineHeight:1.55 }}>
        {a.why_anomalous || '—'}
      </div>
    </div>
  );
}

function FindingCard({ f }) {
  const [expanded, setExpanded] = useState(false);
  const ecoEvents   = f.eco?.events   || [];
  const humanEvents = f.hum?.events   || [];
  const hasEvents = ecoEvents.length > 0 || humanEvents.length > 0;

  // Verdict palette — handles real verdicts + pending/insufficient/error
  const VERDICT_COLOR = {
    PARALLEL:        C.sage,
    'NEAR-PARALLEL': C.gold,
    DIVERGENT:       C.terra,
    INSUFFICIENT:    C.amber,
    PENDING:         C.dim,
    ERROR:           C.terra,
  };
  const VERDICT_BG = {
    PARALLEL:        'rgba(122,158,130,.05)',
    'NEAR-PARALLEL': 'rgba(200,169,110,.05)',
    DIVERGENT:       'rgba(196,80,80,.05)',
    INSUFFICIENT:    'rgba(212,149,106,.05)',
    PENDING:         'rgba(85,85,102,.05)',
    ERROR:           'rgba(196,80,80,.05)',
  };
  const VERDICT_BORDER = {
    PARALLEL:        'rgba(122,158,130,.25)',
    'NEAR-PARALLEL': 'rgba(200,169,110,.25)',
    DIVERGENT:       'rgba(196,80,80,.3)',
    INSUFFICIENT:    'rgba(212,149,106,.25)',
    PENDING:         'rgba(85,85,102,.25)',
    ERROR:           'rgba(196,80,80,.3)',
  };
  const verdictColor  = VERDICT_COLOR[f.verdict]  || C.dim;
  const findingBg     = VERDICT_BG[f.verdict]     || 'rgba(85,85,102,.05)';
  const findingBorder = VERDICT_BORDER[f.verdict] || 'rgba(85,85,102,.25)';

  return (
    <div style={{
      background:C.s1, border:`1px solid ${C.b2}`, borderRadius:8,
      marginBottom:14, overflow:'hidden',
    }}>
      <div style={{
        display:'flex', alignItems:'center', gap:14,
        padding:'12px 18px', background:C.s2,
        borderBottom:`1px solid ${C.b1}`,
      }}>
        <div style={{
          fontFamily:F.mono, fontSize:11, color:C.dim, letterSpacing:'0.06em',
        }}>{f.num}</div>
        <div style={{ fontFamily:F.serif, fontSize:16, fontWeight:300, color:C.text, flex:1 }}>
          {f.name}
        </div>
        <div style={{
          fontSize:9, padding:'3px 10px', borderRadius:4,
          background:`${verdictColor}15`, border:`1px solid ${verdictColor}50`,
          color:verdictColor, letterSpacing:'0.06em', fontWeight:500,
        }}>
          {f.verdict}
        </div>
        <div style={{ fontSize:9, color:C.static, letterSpacing:'0.04em' }}>
          {f.conf}
        </div>
      </div>

      <div style={{ padding:'14px 18px' }}>
        {/* Distribution comparison */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 1fr', gap:14, marginBottom:14, alignItems:'stretch' }}>
          <DistributionSide
            tag="Ecological Systems"
            tagColor={C.sage}
            data={f.eco}
            barColor={C.sage}
          />
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6 }}>
            <div style={{ fontSize:9, color:C.dim, letterSpacing:'0.1em' }}>vs</div>
            <div style={{ fontSize:11, color:f.deltaColor || C.mid, fontFamily:F.mono }}>
              {f.delta}
            </div>
          </div>
          <DistributionSide
            tag="Human Systems"
            tagColor={f.hum.color || C.teal}
            data={f.hum}
            barColor={f.hum.color || C.teal}
          />
        </div>

        {/* Stats row */}
        <div style={{
          display:'flex', alignItems:'center', gap:0, flexWrap:'wrap',
          padding:'8px 12px', background:C.s2, border:`1px solid ${C.b1}`, borderRadius:6,
          marginBottom:12,
        }}>
          {f.stats.map((s, i) => {
            const valColor = s.cls === 'good'  ? C.sage
                         : s.cls === 'warn'  ? C.gold
                         : s.cls === 'terra' ? C.terra
                         : C.mid;
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
                  <span style={{ fontSize:9, color:C.dim, letterSpacing:'0.06em', textTransform:'uppercase' }}>{s.lbl}</span>
                  <span style={{ fontSize:11, color:valColor, fontFamily:F.mono }}>{s.val}</span>
                </div>
                {i < f.stats.length - 1 && <div style={{ width:1, height:14, background:C.b2, margin:'0 14px' }} />}
              </div>
            );
          })}
        </div>

        {/* Plain English finding */}
        <div style={{
          padding:'12px 14px', background:findingBg,
          border:`1px solid ${findingBorder}`, borderRadius:6,
          marginBottom: f.caveat ? 10 : 0,
        }}>
          <div style={{
            fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase',
            color:verdictColor, marginBottom:8, fontWeight:500,
          }}>
            {f.finding.lbl}
          </div>
          <div style={{ fontFamily:F.sans, fontSize:12, color:C.mid, lineHeight:1.7 }}>
            {f.finding.paragraphs
              ? f.finding.paragraphs.map((p, i) => (
                  <p key={i} style={{ marginBottom: i === f.finding.paragraphs.length - 1 ? 0 : 10 }}>{p}</p>
                ))
              : f.finding.text}
          </div>
        </div>

        {/* Caveat */}
        {f.caveat && (
          <div style={{
            display:'flex', alignItems:'flex-start', gap:8,
            padding:'8px 12px', background:C.bg,
            border:`1px solid ${C.b1}`, borderRadius:6,
            fontSize:11, color:C.dim, lineHeight:1.55,
            fontFamily:F.sans,
          }}>
            <span style={{ color:C.gold, flexShrink:0 }}>◌</span>
            {f.caveat}
          </div>
        )}

        {/* Secondary comparison (e.g. extant-only) */}
        {f.secondary && <SecondaryComparison sec={f.secondary} primaryVerdict={f.verdict} verdictColors={{ VERDICT_COLOR, VERDICT_BG, VERDICT_BORDER }} />}

        {/* Drill-down toggle — show contributing events (provenance) */}
        {hasEvents && (
          <div style={{ marginTop: f.caveat ? 10 : 12 }}>
            <button
              onClick={() => setExpanded(v => !v)}
              style={{
                background:'transparent', border:`1px solid ${C.b2}`, borderRadius:5,
                padding:'6px 12px', cursor:'pointer',
                fontFamily:F.mono, fontSize:9, letterSpacing:'0.14em',
                textTransform:'uppercase', color:C.static,
                transition:'color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = C.gold; e.currentTarget.style.borderColor = C.gold; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = C.static; e.currentTarget.style.borderColor = C.b2; }}
            >
              {expanded ? '▾ Hide contributing events' : `▸ Show ${ecoEvents.length + humanEvents.length} contributing events`}
            </button>
            {expanded && (
              <EventTable
                ecoEvents={ecoEvents}
                humanEvents={humanEvents}
                ecoTruncated={f.eco?.eventsTruncated}
                humanTruncated={f.hum?.eventsTruncated}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Secondary comparison block ────────────────────────────
// Shows a parallel verdict computed on a filtered subset (e.g. for
// MVP Threshold: same human side, but eco filtered to "extant only"
// — i.e. excluding local extinctions where current_value = 0). The
// gap between primary and secondary IS itself a finding.
function SecondaryComparison({ sec, primaryVerdict, verdictColors }) {
  const { VERDICT_COLOR, VERDICT_BG, VERDICT_BORDER } = verdictColors;
  const sColor  = VERDICT_COLOR[sec.verdict]  || C.dim;
  const sBg     = VERDICT_BG[sec.verdict]     || 'rgba(85,85,102,.05)';
  const sBorder = VERDICT_BORDER[sec.verdict] || 'rgba(85,85,102,.25)';

  const verdictChanged = sec.verdict !== primaryVerdict;
  const insightText = verdictChanged
    ? `Verdict changes from ${primaryVerdict} to ${sec.verdict} when this filter is applied — the gap IS the finding.`
    : `Same verdict (${sec.verdict}) holds under this filter — the result is robust.`;

  return (
    <div style={{
      marginTop: 10, padding: '10px 14px',
      background: sBg, border: `1px solid ${sBorder}`, borderRadius: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: sColor, fontWeight: 500, flex: 1,
        }}>
          Secondary — {sec.label}
        </div>
        <div style={{
          fontSize: 9, padding: '2px 8px', borderRadius: 4,
          background: `${sColor}15`, border: `1px solid ${sColor}50`,
          color: sColor, letterSpacing: '0.06em', fontWeight: 500,
        }}>
          {sec.verdict}
        </div>
        <div style={{ fontSize: 9, color: C.static, letterSpacing: '0.04em' }}>
          {sec.confidence || '—'}
        </div>
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 16,
        fontFamily: F.mono, fontSize: 10, color: C.mid, marginBottom: 8,
      }}>
        <div>eco n={sec.eco_n ?? '—'}</div>
        <div>eco mean={sec.eco_mean != null ? Number(sec.eco_mean).toFixed(3) : '—'}</div>
        <div>human n={sec.human_n ?? '—'}</div>
        <div>human mean={sec.human_mean != null ? Number(sec.human_mean).toFixed(3) : '—'}</div>
        <div>p={sec.p_value != null ? Number(sec.p_value).toFixed(4) : '—'}</div>
        <div>d={sec.cohens_d != null ? Number(sec.cohens_d).toFixed(3) : '—'}</div>
      </div>
      <div style={{ fontFamily: F.sans, fontSize: 11, color: C.mid, lineHeight: 1.6 }}>
        {insightText}
      </div>
    </div>
  );
}

// ─── Event drill-down table ────────────────────────────────
// Shows one row per contributing event with system_name, source,
// peak/trough/current, and the value that fed the t-test. Defaults
// to top 30 by drawdown (deepest crashes first); user can show all.
function EventTable({ ecoEvents, humanEvents, ecoTruncated, humanTruncated }) {
  const [showAll, setShowAll] = useState(false);
  const PREVIEW = 15;

  const renderSide = (events, label, accent, truncated) => {
    if (events.length === 0) return null;
    // Sort: prefer drawdown_pct desc (deepest crashes first) when present
    // — that's the most "interesting first" metric for crash-recovery
    // dynamics. Fall back to VALUE DESC for dynamics that don't compute
    // drawdown (e.g. Tipping Signature) so the watch-list cases (highest
    // tipping scores) appear at the top.
    const sorted = [...events].sort((a, b) => {
      if (a.drawdown_pct != null && b.drawdown_pct != null) return b.drawdown_pct - a.drawdown_pct;
      return (b.value ?? 0) - (a.value ?? 0);
    });
    const rows = showAll ? sorted : sorted.slice(0, PREVIEW);

    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontFamily:F.mono, fontSize:8, letterSpacing:'0.16em', textTransform:'uppercase',
          color:accent, marginBottom:8, fontWeight:500,
        }}>
          {label} — {events.length} events{truncated ? ' (sampled from larger set)' : ''}
        </div>
        <div style={{
          background:C.bg, border:`1px solid ${C.b1}`, borderRadius:5,
          overflow:'hidden',
        }}>
          <div style={{
            display:'grid',
            gridTemplateColumns:'2fr 0.8fr 0.7fr 0.7fr 0.7fr 0.7fr',
            padding:'7px 12px', borderBottom:`1px solid ${C.b1}`,
            fontFamily:F.mono, fontSize:8, letterSpacing:'0.1em',
            textTransform:'uppercase', color:C.dim,
          }}>
            <div>System</div>
            <div>Source</div>
            <div style={{ textAlign:'right' }}>Peak</div>
            <div style={{ textAlign:'right' }}>Trough</div>
            <div style={{ textAlign:'right' }}>Current</div>
            <div style={{ textAlign:'right' }}>Ratio</div>
          </div>
          {rows.map((e, i) => (
            <div key={i} style={{
              display:'grid',
              gridTemplateColumns:'2fr 0.8fr 0.7fr 0.7fr 0.7fr 0.7fr',
              padding:'6px 12px',
              borderBottom: i < rows.length - 1 ? `1px solid ${C.b3 || C.b2}` : 'none',
              fontFamily:F.mono, fontSize:10, color:C.mid,
              alignItems:'center',
            }}>
              <div style={{ color:C.text, fontFamily:F.sans, fontSize:11, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {e.system_name || '—'}
              </div>
              <div style={{ color:C.dim, fontSize:9 }}>{e.source_key || '—'}</div>
              <div style={{ textAlign:'right' }}>{fmt(e.peak_value)} {e.peak_year ? <span style={{ color:C.dim }}>({e.peak_year})</span> : null}</div>
              <div style={{ textAlign:'right' }}>{fmt(e.trough_value)} {e.trough_year ? <span style={{ color:C.dim }}>({e.trough_year})</span> : null}</div>
              <div style={{ textAlign:'right' }}>{fmt(e.current_value)} {e.current_year ? <span style={{ color:C.dim }}>({e.current_year})</span> : null}</div>
              <div style={{ textAlign:'right', color:accent, fontWeight:500 }}>{e.value != null ? Number(e.value).toFixed(3) : '—'}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const totalEvents = ecoEvents.length + humanEvents.length;
  const previewing = !showAll && totalEvents > PREVIEW * 2;

  return (
    <div style={{ marginTop: 10 }}>
      {renderSide(ecoEvents, 'Ecological', C.sage, ecoTruncated)}
      {renderSide(humanEvents, 'Human', C.terra, humanTruncated)}
      {previewing && (
        <button
          onClick={() => setShowAll(true)}
          style={{
            background:'transparent', border:'none', cursor:'pointer',
            fontFamily:F.mono, fontSize:9, letterSpacing:'0.14em',
            textTransform:'uppercase', color:C.gold, padding:'4px 0',
          }}
        >
          Show all {totalEvents} events ▾
        </button>
      )}
    </div>
  );
}

// Compact numeric formatter — large numbers shorten, small numbers
// keep precision.
function fmt(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const num = Number(n);
  if (Math.abs(num) >= 10000) return num.toExponential(2);
  if (Math.abs(num) >= 100)   return num.toFixed(0);
  if (Math.abs(num) >= 1)     return num.toFixed(2);
  return num.toFixed(3);
}

function DistributionSide({ tag, tagColor, data, barColor }) {
  const hasData = Array.isArray(data.vals) && data.vals.length > 0 && data.vals.some(v => v > 0);
  const max = hasData ? Math.max(...data.vals) : 1;
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
        <div style={{ width:6, height:6, borderRadius:'50%', background:tagColor }} />
        <div style={{ fontSize:9, color:C.static, letterSpacing:'0.06em', textTransform:'uppercase' }}>
          {tag}
        </div>
      </div>
      <div style={{
        display:'flex', alignItems:'flex-end', gap:2, height:54,
        padding:'2px 0',
      }}>
        {hasData ? data.vals.map((v, i) => {
          const h = Math.max(3, Math.round((v / max) * 52));
          const op = 0.4 + i * (0.6 / data.vals.length);
          return (
            <div key={i} style={{
              flex:1, height:h, background:barColor, opacity:op,
              borderRadius:'1px 1px 0 0',
            }} />
          );
        }) : (
          <div style={{
            width:'100%', alignSelf:'center', textAlign:'center',
            fontSize:9, color:C.dim, fontFamily:F.mono, fontStyle:'italic',
          }}>
            no data
          </div>
        )}
      </div>
      <div style={{ marginTop:8 }}>
        <div style={{ fontSize:18, color:data.meanColor || tagColor, fontFamily:F.mono }}>
          {data.mean}
        </div>
        <div style={{ fontSize:10, color:C.dim, marginTop:2 }}>{data.lbl}</div>
        <div style={{ fontSize:9, color:C.static, marginTop:2, fontFamily:F.mono }}>{data.n}</div>
      </div>
    </div>
  );
}

// ─── Findings sidebar ──────────────────────────────────────
function FindingsSidebar({ comparisons = [] }) {
  // Build the Summary Matrix from real comparisons in canonical order
  const byKey = Object.fromEntries(comparisons.map(c => [c.dynamic_key, c]));
  const matrixRows = DYNAMICS.map(dyn => {
    const c = byKey[dyn.key];
    const rawVerdict = c?.verdict || 'PENDING';
    const shortVerdict = rawVerdict === 'NEAR-PARALLEL' ? 'NEAR'
                       : rawVerdict === 'INSUFFICIENT'  ? 'INSUF'
                       : rawVerdict;
    return {
      name: dyn.short,
      verdict: shortVerdict,
      conf: c?.confidence ? c.confidence.slice(0, 4) : '—',
    };
  });

  const verdictColor = (v) => {
    if (v === 'PARALLEL')  return C.sage;
    if (v === 'NEAR')      return C.gold;
    if (v === 'DIVERGENT') return C.terra;
    if (v === 'INSUF')     return C.amber;
    if (v === 'PENDING')   return C.dim;
    if (v === 'ERROR')     return C.terra;
    return C.dim;
  };

  return (
    <div style={{
      borderLeft:`1px solid ${C.b1}`, background:C.s1,
      display:'flex', flexDirection:'column', overflowY:'auto', padding:'16px 14px',
    }}>
      <SidebarSection label="Summary Matrix">
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {matrixRows.map((r, i) => {
            const vColor = verdictColor(r.verdict);
            return (
              <div key={i} style={{
                display:'grid', gridTemplateColumns:'1fr auto auto', gap:8,
                alignItems:'center', padding:'7px 10px',
                background:C.s2, border:`1px solid ${C.b2}`, borderRadius:5,
              }}>
                <div style={{ fontSize:11, color:C.mid, fontFamily:F.sans }}>{r.name}</div>
                <div style={{
                  fontSize:8, padding:'2px 7px', borderRadius:3,
                  background:`${vColor}15`, color:vColor,
                  letterSpacing:'0.06em', fontFamily:F.mono,
                }}>
                  {r.verdict}
                </div>
                <div style={{ fontSize:8, color:C.static, fontFamily:F.mono }}>{r.conf}</div>
              </div>
            );
          })}
        </div>
      </SidebarSection>

      <SidebarSection label="Shift's Reading">
        {SHIFT_NOTES.map((n, i) => (
          <div key={i} style={{
            background: i === 1 ? 'rgba(200,169,110,.05)' : 'rgba(155,143,196,.04)',
            border: `1px solid ${i === 1 ? 'rgba(200,169,110,.14)' : 'rgba(155,143,196,.18)'}`,
            borderRadius:6, padding:'10px 12px', marginBottom:8,
          }}>
            <div style={{
              fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em', textTransform:'uppercase',
              color:n.color, marginBottom:6,
            }}>
              {n.label}
            </div>
            <div style={{ fontFamily:F.sans, fontSize:11, color:C.mid, lineHeight:1.6 }}>
              {n.body}
            </div>
          </div>
        ))}
      </SidebarSection>

      <SidebarSection label="Run History">
        {RUN_HISTORY.map((r, i) => {
          const badgeColor = r.badge === 'HIGH' ? C.sage : r.badge === 'MED' ? C.gold : C.terra;
          return (
            <div key={i} style={{
              display:'grid', gridTemplateColumns:'1fr auto auto', gap:8, alignItems:'center',
              padding:'7px 10px', marginBottom:4,
              background: r.current ? C.s2 : 'transparent',
              border: r.current ? `1px solid ${C.b2}` : `1px solid transparent`,
              borderRadius:5, fontSize:10, fontFamily:F.mono,
            }}>
              <div style={{ color: r.current ? C.gold : C.static }}>{r.date}</div>
              <div style={{ color:C.dim }}>{r.events}</div>
              <div style={{
                fontSize:8, padding:'1px 6px', borderRadius:3,
                background:`${badgeColor}15`, color:badgeColor,
              }}>
                {r.badge}
              </div>
            </div>
          );
        })}
      </SidebarSection>

      <SidebarSection label="Paper Readiness">
        <div style={{
          background:C.s2, border:`1px solid ${C.b2}`, borderRadius:6,
          padding:'10px 12px',
        }}>
          <div style={{
            fontFamily:F.mono, fontSize:8, letterSpacing:'0.14em', textTransform:'uppercase',
            color:C.gold, marginBottom:8,
          }}>
            Toward Publication
          </div>
          {PAPER_READINESS.items.map((it, i) => {
            const dotColor = it.state === 'done' ? C.sage : it.state === 'progress' ? C.gold : C.dim;
            const txtColor = it.state === 'done' ? C.mid : it.state === 'progress' ? C.text : C.dim;
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 0' }}>
                <div style={{
                  width:6, height:6, borderRadius:'50%', flexShrink:0,
                  background:dotColor,
                }} />
                <div style={{ fontSize:11, color:txtColor, fontFamily:F.sans }}>{it.label}</div>
              </div>
            );
          })}
          <div style={{
            marginTop:10, height:3, background:C.b2, borderRadius:2,
          }}>
            <div style={{
              width:`${PAPER_READINESS.pct}%`, height:'100%',
              background:C.gold, borderRadius:2,
            }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6, fontFamily:F.mono, fontSize:9 }}>
            <span style={{ color:C.gold }}>{PAPER_READINESS.pct}% ready</span>
            <span style={{ color:C.dim }}>Target: Month 4</span>
          </div>
        </div>
      </SidebarSection>
    </div>
  );
}

function SidebarSection({ label, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{
        fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase',
        color:C.dim, marginBottom:8, padding:'0 2px',
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SCENARIOS SCREEN — What-If Simulator (Week 1 placeholder)
// ═══════════════════════════════════════════════════════════
// Per docs/CONTINUUM_WHATIF_SPEC.md, this is the user-facing surface
// for the universal what-if engine (lib/intelligence/engines/whatif.js).
//
// Layout: scenario picker + parameter knobs (left) · 12-section
// output (right). Currently shows the architecture with stubbed
// runner outputs — Week 2-7 fills in real distributions, mechanism
// traces, evidence-basis labels, etc.
//
// First scenario: Korea Decentralization (HIGH reflexivity →
// diagnostic_only mode).
// ═══════════════════════════════════════════════════════════

const SCENARIO_LIST = [
  {
    id: 'korea-decentralization',
    name: 'Korea Decentralization',
    short_description:
      'What if 30% of Seoul population redistributed to satellite cities?',
    reflexivity: 'HIGH',
    status: 'week_1_scaffold',
  },
];

function ScenariosScreen({ workspaceId }) {
  const [selectedId, setSelectedId] = useState('korea-decentralization');
  const [parameters, setParameters] = useState({
    redistribution_pct: 30,
    satellite_count: 8,
    satellite_size_target: 400,
    transit_time_to_seoul_min: 90,
    cooperative_infra_seeding_pct: 50,
  });
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  async function runScenario() {
    if (running || !selectedId) return;
    setRunning(true);
    setError(null);
    try {
      const res = await fetch('/api/continuum/whatif', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ scenario_id: selectedId, parameters }),
      });
      const j = await res.json();
      if (!res.ok) {
        setError(j.error || `HTTP ${res.status}`);
      } else {
        setOutput(j.output);
      }
    } catch (err) {
      setError(err.message);
    }
    setRunning(false);
  }

  const selected = SCENARIO_LIST.find(s => s.id === selectedId);

  return (
    <div style={{
      display:'grid', gridTemplateColumns:'320px 1fr',
      height:'100%', overflow:'hidden',
    }}>
      {/* ─── LEFT: scenario picker + knobs ──────────────── */}
      <div style={{
        borderRight:`1px solid ${C.b1}`, background:C.s1,
        display:'flex', flexDirection:'column', overflow:'hidden',
      }}>
        <ColHeader>What-If Scenarios</ColHeader>

        <div style={{ flex:1, overflowY:'auto', padding:14 }}>
          {/* Scenario picker */}
          <div style={{ marginBottom:18 }}>
            <div style={{
              fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em',
              textTransform:'uppercase', color:C.dim, marginBottom:8,
            }}>
              Scenario
            </div>
            {SCENARIO_LIST.map(s => {
              const on = selectedId === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => { setSelectedId(s.id); setOutput(null); }}
                  style={{
                    width:'100%', textAlign:'left',
                    padding:'10px 12px', marginBottom:6,
                    background: on ? 'rgba(200,169,110,.1)' : C.s2,
                    border:`1px solid ${on ? 'rgba(200,169,110,.3)' : C.b2}`,
                    borderRadius:4, cursor:'pointer',
                    fontFamily:F.mono,
                  }}
                >
                  <div style={{
                    fontSize:11, color: on ? C.gold : C.text,
                    marginBottom:3, fontWeight:500,
                  }}>
                    {s.name}
                  </div>
                  <div style={{ fontSize:9, color:C.static, lineHeight:1.4 }}>
                    {s.short_description}
                  </div>
                  <div style={{
                    fontSize:8, color:C.terra, marginTop:5, letterSpacing:'0.08em',
                  }}>
                    Reflexivity: {s.reflexivity} → diagnostic-only output
                  </div>
                </button>
              );
            })}
          </div>

          {/* Parameter knobs */}
          {selected && (
            <div style={{ marginBottom:18 }}>
              <div style={{
                fontFamily:F.mono, fontSize:8, letterSpacing:'0.18em',
                textTransform:'uppercase', color:C.dim, marginBottom:8,
              }}>
                Parameters
              </div>
              <KnobRow label="Redistribution %"               value={parameters.redistribution_pct}            min={10}  max={50}  step={5}
                onChange={v => setParameters(p => ({ ...p, redistribution_pct: v }))} />
              <KnobRow label="Satellite count"                value={parameters.satellite_count}               min={3}   max={12}  step={1}
                onChange={v => setParameters(p => ({ ...p, satellite_count: v }))} />
              <KnobRow label="Per-satellite size (×1000)"     value={parameters.satellite_size_target}         min={100} max={800} step={50}
                onChange={v => setParameters(p => ({ ...p, satellite_size_target: v }))} />
              <KnobRow label="Transit to Seoul (min)"         value={parameters.transit_time_to_seoul_min}     min={30}  max={180} step={15}
                onChange={v => setParameters(p => ({ ...p, transit_time_to_seoul_min: v }))} />
              <KnobRow label="Cooperative infra seeding %"    value={parameters.cooperative_infra_seeding_pct} min={0}   max={100} step={10}
                onChange={v => setParameters(p => ({ ...p, cooperative_infra_seeding_pct: v }))} />
            </div>
          )}

          {/* Run button */}
          <button
            onClick={runScenario}
            disabled={running || !selectedId}
            style={{
              width:'100%', padding:'8px 12px',
              background: running ? 'rgba(90,158,181,.12)' : 'rgba(200,169,110,.12)',
              border: `1px solid ${running ? 'rgba(90,158,181,.25)' : 'rgba(200,169,110,.3)'}`,
              color: running ? C.teal : C.gold,
              borderRadius:4, cursor: running ? 'not-allowed' : 'pointer',
              fontFamily:F.mono, fontSize:10, letterSpacing:'0.06em',
            }}
          >
            {running ? '⟳ Running…' : '▶ Run Scenario'}
          </button>

          {error && (
            <div style={{
              marginTop:10, padding:'8px 10px',
              background:'rgba(196,96,80,.1)', border:`1px solid ${C.terra}`,
              borderRadius:4, fontSize:9, color:C.terra, fontFamily:F.mono,
            }}>
              ⚠ {error}
            </div>
          )}

          <div style={{
            marginTop:14, padding:'8px 10px',
            background:'rgba(122,158,130,.08)', border:`1px dashed ${C.sage}`,
            borderRadius:4, fontSize:9, color:C.dim,
            fontFamily:F.mono, lineHeight:1.5,
          }}>
            All 14 weeks shipped. 12-section output live: real distributions
            (replay + statistical + mechanistic), mechanism trace, evidence
            basis, reflexivity dispatch, statistical rigor, pre-registration,
            governance, drift detection. Run a fresh simulation to populate
            every section. Cached runs from before the engine commits show
            placeholders for sections 6–8. See docs/CONTINUUM_WHATIF_SPEC.md.
          </div>
        </div>
      </div>

      {/* ─── RIGHT: 12-section output ──────────────────── */}
      <div style={{ overflow:'auto', padding:18, background:C.bg }}>
        {!output && !running && (
          <EmptyOutputHero scenarioName={selected?.name} />
        )}
        {running && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'center',
            height:'100%', color:C.teal, fontFamily:F.mono, fontSize:13,
          }}>
            <span style={{
              display:'inline-block', marginRight:10,
              animation:'continuumPulse 1s infinite',
            }}>⟳</span>
            Running scenario…
          </div>
        )}
        {output && <WhatIfOutputRenderer output={output} />}
      </div>
    </div>
  );
}

function KnobRow({ label, value, min, max, step, onChange }) {
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{
        display:'flex', justifyContent:'space-between',
        fontFamily:F.mono, fontSize:9, marginBottom:3,
      }}>
        <span style={{ color:C.mid }}>{label}</span>
        <span style={{ color:C.gold }}>{value}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width:'100%', accentColor:C.gold, cursor:'pointer' }}
      />
    </div>
  );
}

function EmptyOutputHero({ scenarioName }) {
  return (
    <div style={{
      display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', height:'100%', textAlign:'center',
      color:C.static, fontFamily:F.mono,
    }}>
      <div style={{ fontSize:32, marginBottom:14, opacity:0.4 }}>⟁</div>
      <div style={{ fontSize:13, color:C.mid, marginBottom:6 }}>
        {scenarioName || 'Pick a scenario'}
      </div>
      <div style={{ fontSize:10, maxWidth:380, lineHeight:1.6 }}>
        Adjust the parameters on the left and press <span style={{ color:C.gold }}>▶ Run Scenario</span>.
        Output renders the 12-section structure: tipping-point proximity,
        intervention gating, probability distribution, conditions for
        best/worst case, mechanism trace, prescriptions with evidence
        basis, ethical check, confidence, reflexivity rating, Track Record link.
      </div>
    </div>
  );
}

// ─── 12-section renderer ────────────────────────────────
// Each section detects whether it has REAL data or is STUBBED.
// Real data renders as proper visualization (tables, key-value pairs).
// Stubbed sections render a clean placeholder with arrival week —
// never raw JSON dumps of empty arrays or null values.

function WhatIfOutputRenderer({ output }) {
  const ss = output.system_state || {};
  const hasLoadedVars = Array.isArray(ss.loaded_variables) && ss.loaded_variables.length > 0;
  const hasTipping = Array.isArray(ss.tipping_points) && ss.tipping_points.length > 0;

  const ig = output.intervention_gating || {};
  const hasGating = (ig.still_effective?.length || ig.too_late?.length || ig.sequencing_required?.length) > 0;

  const hasDistribution = output.distribution !== null && output.distribution !== undefined;

  const mt = output.mechanism_trace || {};
  const hasTrace = (mt.eco_chain?.length || mt.human_chain?.length) > 0;

  const hasConfidence = output.confidence?.breakdown && Object.keys(output.confidence.breakdown).length > 0
    && output.confidence.breakdown.runners !== 'all stubbed';

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12, fontFamily:F.mono }}>
      <OutputHeader output={output} />

      {/* Section 1: System State — split into loaded vars (real) + tipping (stub) */}
      <OutputSection num={1} title="System State — Tipping-Point Proximity" arrives="Week 4-5">
        {hasLoadedVars && <LoadedVariablesTable variables={ss.loaded_variables} sources={ss.loaded_from} asOf={ss.as_of} />}
        {hasTipping
          ? <TippingPointsList tipping={ss.tipping_points} />
          : <StubPlaceholder
              message="Tipping-point proximity calculator not yet wired. When live, this will show proximity (with CI) per relevant threshold — Allee, carrying capacity, age structure inversion, R/K mismatch, phosphorus depletion — using the loaded variables above."
              arrives="Week 4-5" />}
      </OutputSection>

      {/* Section 2: Intervention Gating */}
      <OutputSection num={2} title="Intervention Gating" arrives="Week 5">
        {hasGating
          ? <InterventionGatingView gating={ig} />
          : <StubPlaceholder
              message="Will list which interventions are still effective vs. too-late given current tipping-point state. Requires Section 1 tipping detection first."
              arrives="Week 5" />}
      </OutputSection>

      {/* Section 3: Probability Distribution + analogs + statistical + mechanistic + cross-runner */}
      <OutputSection num={3} title="Probability Distribution of Outcome" arrives="Week 2 (replay) · Week 3 (statistical) · Week 4 (mechanistic) · Week 9 (rigor)">
        {hasDistribution
          ? <>
              <DistributionView
                distribution={output.distribution}
                analogs={output.analogs_matched}
                warning={output.extrapolation_warning} />
              {output.statistical_projection && (
                <StatisticalProjectionView
                  regression={output.statistical_regression}
                  projection={output.statistical_projection}
                  distribution={output.statistical_distribution} />
              )}
              {output.mechanistic_distribution && (
                <MechanisticView
                  distribution={output.mechanistic_distribution}
                  modelRuns={output.mechanistic_model_runs} />
              )}
              {output.cross_runner && (
                <CrossRunnerView cross_runner={output.cross_runner} />
              )}
            </>
          : <StubPlaceholder
              message="Will show distribution chart of TFR @ year 20 with CI band, sample size N, bootstrap intervals, null-analog comparison. Requires the three runners (replay/statistical/mechanistic) to be implemented."
              arrives="Week 2-4 (runners) + Week 9 (statistical rigor)" />}
      </OutputSection>

      {/* Section 4 + 5: Conditions (now with mechanism + count + citing analogs) */}
      <OutputSection num={4} title="Conditions for Best-Case Outcome" arrives="Week 2 (from replay successes) · Week 5 (full)">
        {output.best_case_conditions?.length
          ? <ConditionsView conditions={output.best_case_conditions} tone="best" />
          : <StubPlaceholder
              message="Checklist of what must be true for best-case outcome to materialize. Derived from runner agreement zones + matched analog patterns."
              arrives="Week 5" />}
      </OutputSection>
      <OutputSection num={5} title="Conditions Making Worst-Case Likely" arrives="Week 2 (from replay failures) · Week 5 (full)">
        {output.worst_case_conditions?.length
          ? <ConditionsView conditions={output.worst_case_conditions} tone="worst" />
          : <StubPlaceholder
              message="Warning checklist of conditions that would tip the scenario toward worst-case. Mirror of Section 4 — derived from disagreement zones + failure-pattern analogs."
              arrives="Week 5" />}
      </OutputSection>

      {/* Section 6: Mechanism Trace */}
      <OutputSection num={6} title="Mechanism Trace" arrives="Week 5">
        {hasTrace
          ? <MechanismTraceView trace={mt} />
          : <StubPlaceholder
              message="Will show the explicit causal chain: ecological mechanism → human translation, link by link, with per-link evidence + confidence. The chain is only as strong as its weakest evidenced link (failure-mode #1 mitigation)."
              arrives="Week 5" />}
      </OutputSection>

      {/* Section 7: Prescriptions (mode-aware) */}
      <OutputSection num={7} title={`Ecological Prescription — mode: ${output.reflexivity?.mode_applied || output.reflexivity?.mode || 'unrated'}`} arrives="Week 6-7">
        {output.prescriptions?.length
          ? <PrescriptionsView
              prescriptions={output.prescriptions}
              reflexivity={output.reflexivity} />
          : <StubPlaceholder
              message={
                output.reflexivity?.mode === 'diagnostic_only'
                  ? 'Korea is rated HIGH reflexivity → diagnostic_only mode is correct. When this fills in (Week 6-7), it will list the tested-prescription menu (with evidence-basis labels) instead of direct "do this" recommendations — letting policy actors derive their own response.'
                  : 'Will list ecological prescriptions sequenced by tipping-point gating. Each carries an evidence-basis label (EMPIRICAL / ANALOG / EXTRAPOLATIVE).'
              }
              arrives="Week 6-7" />}
      </OutputSection>

      {/* Section 8: Evidence Basis */}
      <OutputSection num={8} title="Evidence Basis per Prescription" arrives="Week 6">
        {output.evidence_basis?.length
          ? <EvidenceBasisView items={output.evidence_basis} />
          : <StubPlaceholder
              message="Each prescription gets a label: EMPIRICAL (3+ tested human cases) · ANALOG (ecology evidence + plausible translation) · EXTRAPOLATIVE (mechanism-only, treat as hypothesis). Distinct visual treatment per label so users can't mistake hypothesis for evidence."
              arrives="Week 6" />}
      </OutputSection>

      {/* Section 9: Ethical Check (REAL DATA from scenario config) */}
      <OutputSection num={9} title="Ethical Check" arrives="Week 7-8 (governance layer)">
        <EthicalCheckView ec={output.ethical_check} />
      </OutputSection>

      {/* Section 10: Confidence */}
      <OutputSection num={10} title={`Confidence — ${output.confidence?.overall || 'low'}`} arrives="Week 5-9">
        {hasConfidence
          ? <ConfidenceView c={output.confidence} />
          : <StubPlaceholder
              message={`Currently flagged as "low" because all runners are stubbed. When real runners come online, confidence will break down per component (mechanism / data / extrapolation / cultural) with explicit "what would change my mind" disclosure.`}
              arrives="Week 5-9" />}
      </OutputSection>

      {/* Section 11: Reflexivity (REAL DATA from scenario config) */}
      <OutputSection num={11} title="Reflexivity Rating + Output Mode" arrives="Week 7 (dispatcher logic)">
        <ReflexivityView r={output.reflexivity} />
      </OutputSection>

      {/* Section 12: Track Record */}
      <OutputSection num={12} title="Track Record" arrives="Week 11">
        <StubPlaceholder
          message={`Public calibration log including failed predictions. Will live at ${output.track_record_link}. Each prediction logged with timestamp + conditions + expected outcome + time horizon. Quarterly outcome-update job populates actuals + calibration tag.`}
          arrives="Week 11" />
      </OutputSection>
    </div>
  );
}

// ─── REAL-DATA RENDERERS ─────────────────────────────────

function StubPlaceholder({ message, arrives }) {
  return (
    <div style={{
      padding:'14px 16px',
      background:'rgba(91,143,168,.04)',
      border:`1px dashed ${C.b3}`,
      borderRadius:4,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <span style={{ fontSize:13, color:C.dim }}>⟁</span>
        <span style={{
          fontSize:8, padding:'2px 7px', borderRadius:3,
          background:'rgba(155,143,196,.1)', color:C.purple,
          letterSpacing:'0.08em',
        }}>STUBBED</span>
        <span style={{ fontSize:9, color:C.gold, marginLeft:'auto', letterSpacing:'0.06em' }}>arrives: {arrives}</span>
      </div>
      <div style={{ fontSize:10, color:C.static, lineHeight:1.55 }}>{message}</div>
    </div>
  );
}

function LoadedVariablesTable({ variables, sources, asOf }) {
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{
        display:'flex', alignItems:'center', gap:10, marginBottom:8,
      }}>
        <span style={{
          fontSize:8, padding:'2px 7px', borderRadius:3,
          background:'rgba(122,158,130,.1)', color:C.sage,
          letterSpacing:'0.08em',
        }}>LIVE DATA</span>
        <span style={{ fontSize:9, color:C.dim }}>
          {variables.length} variables · loaded from {(sources || []).map(s => s.source).join(', ')}
          {asOf && <> · as of {String(asOf).slice(0, 10)}</>}
        </span>
      </div>
      <div style={{
        background:C.bg, border:`1px solid ${C.b1}`, borderRadius:4, overflow:'hidden',
      }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
          <thead>
            <tr style={{ background:C.s2 }}>
              <th style={thStyle}>variable</th>
              <th style={thStyle}>value</th>
              <th style={thStyle}>unit</th>
              <th style={thStyle}>as of</th>
              <th style={thStyle}>source</th>
            </tr>
          </thead>
          <tbody>
            {variables.map((v, i) => (
              <tr key={i} style={{ borderTop:`1px solid ${C.b1}` }}>
                <td style={tdStyle}>{v.name}</td>
                <td style={{ ...tdStyle, color:C.gold, fontVariantNumeric:'tabular-nums' }}>{formatValue(v.value)}</td>
                <td style={tdStyle}>{v.unit}</td>
                <td style={tdStyle}>{v.as_of?.slice(0, 4) || '—'}</td>
                <td style={tdStyle}>{v.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DistributionView({ distribution, analogs, warning }) {
  const d = distribution;
  if (!d) return null;
  return (
    <div>
      {/* Live-data badge + extrapolation warning above the numbers */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <span style={{
          fontSize:8, padding:'2px 7px', borderRadius:3,
          background:'rgba(122,158,130,.1)', color:C.sage,
          letterSpacing:'0.08em',
        }}>LIVE — REPLAY RUNNER</span>
        <span style={{ fontSize:9, color:C.dim }}>
          N={d.n} analogs · method: {d.method}
        </span>
      </div>

      {warning && (
        <div style={{
          padding:'10px 12px', marginBottom:10,
          background:'rgba(196,96,80,.08)', border:`1px solid ${C.terra}`,
          borderRadius:4, fontSize:10, color:C.mid, lineHeight:1.55,
        }}>
          <div style={{ fontSize:9, color:C.terra, marginBottom:4, letterSpacing:'0.06em' }}>
            ⚠ EXTRAPOLATION WARNING — {warning.level}
          </div>
          {warning.message}
        </div>
      )}

      {/* Distribution stats */}
      <div style={{
        display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:8, marginBottom:12,
      }}>
        {[
          { label:'mean',   value:d.mean   },
          { label:'median', value:d.median },
          { label:'std',    value:d.std    },
          { label:'min',    value:d.min    },
          { label:'max',    value:d.max    },
        ].map(s => (
          <div key={s.label} style={{
            padding:8, background:C.bg, border:`1px solid ${C.b1}`, borderRadius:4,
            textAlign:'center',
          }}>
            <div style={{ fontSize:8, color:C.dim, letterSpacing:'0.08em' }}>{s.label}</div>
            <div style={{ fontSize:13, color:C.gold, fontVariantNumeric:'tabular-nums', marginTop:2 }}>
              {typeof s.value === 'number' ? s.value.toFixed(2) : '—'}
            </div>
          </div>
        ))}
      </div>

      {/* Percentile band */}
      {d.percentiles && (
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:9, color:C.dim, marginBottom:4, letterSpacing:'0.06em' }}>
            percentile band (unweighted; bootstrap CIs Week 9)
          </div>
          <div style={{ display:'flex', gap:8, fontSize:10, color:C.mid }}>
            {Object.entries(d.percentiles).map(([k, v]) => (
              <span key={k} style={{ flex:1, textAlign:'center', padding:'4px 6px', background:C.bg, border:`1px solid ${C.b1}`, borderRadius:3 }}>
                <span style={{ color:C.dim }}>{k}: </span>
                <span style={{ color:C.gold }}>{typeof v === 'number' ? v.toFixed(2) : '—'}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Analogs matched table */}
      {Array.isArray(analogs) && analogs.length > 0 && (
        <div>
          <div style={{ fontSize:9, color:C.gold, marginBottom:6, letterSpacing:'0.06em' }}>
            analogs matched (sorted by similarity)
          </div>
          <div style={{ background:C.bg, border:`1px solid ${C.b1}`, borderRadius:4, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
              <thead>
                <tr style={{ background:C.s2 }}>
                  <th style={thStyle}>analog</th>
                  <th style={thStyle}>year</th>
                  <th style={thStyle}>similarity</th>
                  <th style={thStyle}>outcome</th>
                  <th style={thStyle}>TFR post</th>
                </tr>
              </thead>
              <tbody>
                {analogs.map((a, i) => {
                  const outColor = a.outcome_class === 'success' ? C.sage
                    : a.outcome_class === 'partial_success' ? C.gold
                    : a.outcome_class === 'failure' ? C.terra
                    : C.dim;
                  return (
                    <tr key={i} style={{ borderTop:`1px solid ${C.b1}` }}>
                      <td style={tdStyle}>{a.name}</td>
                      <td style={tdStyle}>{a.intervention_year}</td>
                      <td style={{ ...tdStyle, color:C.gold }}>{a.similarity?.toFixed(2)}</td>
                      <td style={{ ...tdStyle, color:outColor }}>{a.outcome_class}</td>
                      <td style={{ ...tdStyle, fontVariantNumeric:'tabular-nums' }}>
                        {typeof a.outcome_value === 'number' ? a.outcome_value.toFixed(2) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatisticalProjectionView({ regression, projection, distribution }) {
  if (!regression || !projection) return null;
  return (
    <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${C.b1}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <span style={{
          fontSize:8, padding:'2px 7px', borderRadius:3,
          background:'rgba(122,158,130,.1)', color:C.sage,
          letterSpacing:'0.08em',
        }}>LIVE — STATISTICAL RUNNER</span>
        <span style={{ fontSize:9, color:C.dim }}>
          N={regression.n_countries} peer countries · log-linear regression + bootstrap
        </span>
      </div>

      {/* Regression card */}
      <div style={{
        padding:10, marginBottom:10, background:C.bg,
        border:`1px solid ${C.b1}`, borderRadius:4,
      }}>
        <div style={{ fontSize:9, color:C.dim, marginBottom:6, letterSpacing:'0.06em' }}>regression</div>
        <div style={{ fontSize:10, color:C.mid, fontFamily:F.mono }}>
          {regression.formula}
        </div>
        <div style={{
          display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8, marginTop:8,
        }}>
          {[
            { label:'slope β',   value:regression.slope, ci:regression.slope_ci_95 },
            { label:'intercept', value:regression.intercept },
            { label:'R²',        value:regression.r_squared },
            { label:'bootstrap', value:`${regression.bootstrap_iterations}×` },
          ].map(m => (
            <div key={m.label} style={{
              padding:6, background:C.s2, border:`1px solid ${C.b1}`, borderRadius:3,
              textAlign:'center',
            }}>
              <div style={{ fontSize:8, color:C.dim, letterSpacing:'0.08em' }}>{m.label}</div>
              <div style={{ fontSize:11, color:C.gold, marginTop:2, fontVariantNumeric:'tabular-nums' }}>
                {typeof m.value === 'number' ? m.value.toFixed(3) : m.value}
              </div>
              {m.ci && (
                <div style={{ fontSize:8, color:C.dim, marginTop:2 }}>
                  95% CI: [{m.ci.p05}, {m.ci.p95}]
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Projection */}
      <div style={{
        padding:10, background:C.bg, border:`1px solid ${C.b1}`, borderRadius:4,
      }}>
        <div style={{ fontSize:9, color:C.dim, marginBottom:6, letterSpacing:'0.06em' }}>
          counterfactual projection
        </div>
        <div style={{ fontSize:10, color:C.mid, lineHeight:1.7 }}>
          density: <span style={{ color:C.gold }}>{projection.current_density.toFixed(0)}</span> →{' '}
          <span style={{ color:C.gold }}>{projection.counterfactual_density}</span> ppl/km²
          <br />
          implied TFR: <span style={{ color:C.gold }}>{projection.current_tfr_predicted}</span> →{' '}
          <span style={{ color:C.gold }}>{projection.counterfactual_tfr_predicted}</span>
          {' '}(Δ <span style={{ color: projection.delta_tfr > 0 ? C.sage : C.terra }}>
            {projection.delta_tfr > 0 ? '+' : ''}{projection.delta_tfr}
          </span>)
        </div>
        <div style={{ fontSize:9, color:C.dim, marginTop:6 }}>
          95% CI on counterfactual TFR: [{projection.counterfactual_ci_95.p05}, {projection.counterfactual_ci_95.p95}]
        </div>
      </div>
    </div>
  );
}

function MechanisticView({ distribution, modelRuns }) {
  if (!distribution) return null;
  return (
    <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${C.b1}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <span style={{
          fontSize:8, padding:'2px 7px', borderRadius:3,
          background:'rgba(122,158,130,.1)', color:C.sage,
          letterSpacing:'0.08em',
        }}>LIVE — MECHANISTIC RUNNER</span>
        <span style={{ fontSize:9, color:C.dim }}>tipping-point gated dynamics simulation</span>
      </div>

      {/* Distribution stats */}
      <div style={{
        display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:8, marginBottom:10,
      }}>
        {[
          { label:'mean',   value:distribution.mean   },
          { label:'median', value:distribution.median },
          { label:'std',    value:distribution.std    },
          { label:'p05',    value:distribution.percentiles?.p05 },
          { label:'p95',    value:distribution.percentiles?.p95 },
        ].map(s => (
          <div key={s.label} style={{
            padding:8, background:C.bg, border:`1px solid ${C.b1}`, borderRadius:4,
            textAlign:'center',
          }}>
            <div style={{ fontSize:8, color:C.dim, letterSpacing:'0.08em' }}>{s.label}</div>
            <div style={{ fontSize:13, color:C.gold, fontVariantNumeric:'tabular-nums', marginTop:2 }}>
              {typeof s.value === 'number' ? s.value.toFixed(2) : '—'}
            </div>
          </div>
        ))}
      </div>

      {/* Model run details */}
      {modelRuns && (
        <div style={{
          padding:10, background:C.bg, border:`1px solid ${C.b1}`, borderRadius:4,
        }}>
          <div style={{ fontSize:9, color:C.dim, marginBottom:6, letterSpacing:'0.06em' }}>model run breakdown</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {[
              { label:'baseline density-shift TFR', value:modelRuns.baseline_density_shift_tfr },
              { label:'compound effectiveness multiplier', value:modelRuns.compound_effectiveness_multiplier },
              { label:'adjusted TFR shift', value:modelRuns.adjusted_tfr_shift },
              { label:'projected TFR @ year 20', value:modelRuns.projected_tfr_year_20 },
            ].map(m => (
              <div key={m.label} style={{ fontSize:10, color:C.mid }}>
                <span style={{ color:C.dim }}>{m.label}:</span>
                {' '}
                <span style={{ color:C.gold, fontVariantNumeric:'tabular-nums' }}>{typeof m.value === 'number' ? m.value.toFixed(4) : m.value}</span>
              </div>
            ))}
          </div>
          {modelRuns.reasoning && (
            <div style={{
              marginTop:8, paddingTop:8, borderTop:`1px solid ${C.b1}`,
              fontSize:10, color:C.mid, lineHeight:1.55,
            }}>
              {modelRuns.reasoning}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CrossRunnerView({ cross_runner }) {
  if (!cross_runner) return null;
  const cr = cross_runner;
  const agColor = cr.agreement === 'high' ? C.sage : cr.agreement === 'medium' ? C.gold : C.terra;
  // Build runner cells dynamically based on what's present
  const runnerCells = [];
  if (cr.replay_mean !== null && cr.replay_mean !== undefined) {
    runnerCells.push({ label: 'replay mean', value: cr.replay_mean });
  }
  if (cr.statistical_mean !== null && cr.statistical_mean !== undefined) {
    runnerCells.push({ label: 'statistical mean', value: cr.statistical_mean });
  }
  if (cr.mechanistic_mean !== null && cr.mechanistic_mean !== undefined) {
    runnerCells.push({ label: 'mechanistic mean', value: cr.mechanistic_mean });
  }
  // Spread cell at the end
  runnerCells.push({ label: cr.runners_compared >= 3 ? 'spread' : 'delta', value: cr.spread || cr.delta, accent: true });

  const cols = `repeat(${runnerCells.length}, 1fr)`;

  return (
    <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${C.b1}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <span style={{
          fontSize:8, padding:'2px 7px', borderRadius:3,
          background:`rgba(255,255,255,.05)`, color:agColor,
          letterSpacing:'0.08em',
        }}>CROSS-RUNNER AGREEMENT: {cr.agreement.toUpperCase()}</span>
        <span style={{ fontSize:9, color:C.dim }}>{cr.runners_compared} runners compared</span>
      </div>
      <div style={{ padding:10, background:C.bg, border:`1px solid ${C.b1}`, borderRadius:4 }}>
        <div style={{ display:'grid', gridTemplateColumns:cols, gap:8, marginBottom:8 }}>
          {runnerCells.map((rc, i) => (
            <div key={i} style={{ textAlign:'center' }}>
              <div style={{ fontSize:8, color:C.dim, letterSpacing:'0.08em' }}>{rc.label}</div>
              <div style={{
                fontSize:13, marginTop:2, fontVariantNumeric:'tabular-nums',
                color: rc.accent ? agColor : C.gold,
              }}>
                {typeof rc.value === 'number' ? rc.value.toFixed(2) : rc.value}
                {rc.accent && cr.relative_spread_pct != null && ` (${cr.relative_spread_pct}%)`}
              </div>
            </div>
          ))}
        </div>
        <div style={{ fontSize:10, color:C.mid, lineHeight:1.55, marginTop:6 }}>
          {cr.interpretation}
        </div>
      </div>
    </div>
  );
}

function ConditionsView({ conditions, tone }) {
  const accent = tone === 'best' ? C.sage : C.terra;
  return (
    <div>
      {conditions.map((c, i) => (
        <div key={i} style={{
          padding:'8px 10px', marginBottom:6,
          background:C.bg, border:`1px solid ${C.b1}`, borderRadius:4,
        }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
            <span style={{ fontSize:10, color:C.text }}>{c.mechanism}</span>
            <span style={{ fontSize:9, color:accent, fontVariantNumeric:'tabular-nums' }}>
              {c.count}× cited
            </span>
          </div>
          <div style={{ fontSize:9, color:C.dim }}>
            from: {(c.citing_analogs || []).map(a => a.name || a.id).join(' · ')}
          </div>
        </div>
      ))}
    </div>
  );
}

function TippingPointsList({ tipping }) {
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <span style={{
          fontSize:8, padding:'2px 7px', borderRadius:3,
          background:'rgba(122,158,130,.1)', color:C.sage,
          letterSpacing:'0.08em',
        }}>LIVE — TIPPING DETECTOR</span>
        <span style={{ fontSize:9, color:C.dim }}>
          {tipping.filter(t => t.classification === 'past').length} of {tipping.length} thresholds PAST
        </span>
      </div>
      {tipping.map((tp, i) => {
        const cColor = tp.classification === 'past' ? C.terra
                     : tp.classification === 'approaching' ? C.gold
                     : tp.classification === 'far' ? C.sage : C.dim;
        const cIcon = tp.classification === 'past' ? '🔴'
                    : tp.classification === 'approaching' ? '🟡'
                    : tp.classification === 'far' ? '🟢' : '⚪';
        return (
          <div key={i} style={{
            padding:10, background:C.bg, border:`1px solid ${tp.classification === 'past' ? C.terra : C.b1}`,
            borderRadius:4, marginBottom:8,
          }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:11 }}>{cIcon}</span>
                <span style={{ fontSize:10, color:C.text }}>{tp.name}</span>
              </div>
              <span style={{
                fontSize:8, padding:'2px 7px', borderRadius:3,
                background:'rgba(255,255,255,.05)', color:cColor,
                letterSpacing:'0.08em',
              }}>{tp.classification.toUpperCase()}</span>
            </div>
            <div style={{ fontSize:9, color:C.dim, marginBottom:5 }}>
              {tp.short_description}
            </div>
            <div style={{ fontSize:9, color:C.mid, marginBottom:6 }}>
              proximity: <span style={{ color:cColor }}>{tp.proximity_score}</span>
              {' · '}
              convergence: <span style={{ color:C.gold }}>{tp.convergence}</span>
              {' '}({tp.proxy_breakdown?.past || 0}/{tp.proxies_used} past)
              {' · '}
              confidence: <span style={{ color:C.gold }}>{tp.confidence?.level} ({tp.confidence?.score})</span>
              {' · '}
              time: <span style={{ color:C.dim }}>{tp.time_to_outcome_years?.min}-{tp.time_to_outcome_years?.max}yr</span>
            </div>
            {tp.proxies && tp.proxies.length > 0 && (
              <div style={{ marginTop:5, paddingTop:5, borderTop:`1px solid ${C.b1}` }}>
                <div style={{ fontSize:8, color:C.dim, letterSpacing:'0.06em', marginBottom:3 }}>proxies</div>
                {tp.proxies.map((p, j) => {
                  const pColor = p.classification === 'past' ? C.terra
                               : p.classification === 'approaching' ? C.gold
                               : p.classification === 'far' ? C.sage : C.dim;
                  return (
                    <div key={j} style={{ fontSize:9, color:C.mid, lineHeight:1.5, display:'flex', justifyContent:'space-between' }}>
                      <span>
                        {p.value !== null ? <span style={{ color:pColor }}>● </span> : <span style={{ color:C.dim }}>○ </span>}
                        {p.label}: <span style={{ color:C.gold, fontVariantNumeric:'tabular-nums' }}>{p.value ?? '—'}</span> {p.unit}
                      </span>
                      <span style={{ color:C.dim }}>rel {p.reliability}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function InterventionGatingView({ gating }) {
  const sections = [
    { label: '✗ Too Late',          items: gating.too_late || [],          color: C.terra, badge: 'TOO LATE' },
    { label: '✓ Still Effective',   items: gating.still_effective || [],   color: C.sage,  badge: 'EFFECTIVE' },
    { label: '⚠ Sequencing Required', items: gating.sequencing_required || [], color: C.gold,  badge: 'SEQUENCING' },
  ];
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <span style={{
          fontSize:8, padding:'2px 7px', borderRadius:3,
          background:'rgba(122,158,130,.1)', color:C.sage,
          letterSpacing:'0.08em',
        }}>LIVE — MECHANISTIC RUNNER</span>
        <span style={{ fontSize:9, color:C.dim }}>
          {gating.too_late?.length || 0} too late · {gating.still_effective?.length || 0} effective · {gating.sequencing_required?.length || 0} sequencing
        </span>
      </div>
      {sections.map(s => s.items.length > 0 && (
        <div key={s.label} style={{ marginBottom:10 }}>
          <div style={{
            fontSize:9, color:s.color, marginBottom:5, letterSpacing:'0.06em',
            paddingBottom:4, borderBottom:`1px solid ${C.b1}`,
          }}>{s.label} ({s.items.length})</div>
          {s.items.map((item, i) => (
            <div key={i} style={{
              padding:'7px 9px', marginBottom:5,
              background:C.bg, border:`1px solid ${C.b1}`, borderRadius:3,
              fontSize:10, color:C.mid, lineHeight:1.5,
            }}>
              {item.intervention_class && (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                  <span style={{
                    fontSize:8, padding:'1px 6px', borderRadius:3,
                    background:`rgba(255,255,255,.05)`, color:s.color,
                    letterSpacing:'0.06em',
                  }}>{item.intervention_class.replace(/_/g, ' ')}</span>
                  {typeof item.proximity_score === 'number' && (
                    <span style={{ fontSize:8, color:C.dim, marginLeft:'auto' }}>
                      proximity: <span style={{ color:s.color }}>{item.proximity_score}</span>
                    </span>
                  )}
                </div>
              )}
              {item.sequence && (
                <div style={{ fontSize:10, color:C.text, marginBottom:3 }}>{item.sequence}</div>
              )}
              <div style={{ fontSize:9, color:C.mid }}>
                {item.message || item.reasoning}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function MechanismTraceView({ trace }) {
  return (
    <div>
      {['eco_chain', 'human_chain'].map(side => (
        <div key={side} style={{ marginBottom:10 }}>
          <div style={{ fontSize:9, color:C.gold, marginBottom:5, letterSpacing:'0.06em' }}>
            {side === 'eco_chain' ? 'Ecological mechanism chain' : 'Human translation chain'}
          </div>
          {(trace[side] || []).map((link, i) => (
            <div key={i} style={{
              padding:8, background:C.bg, border:`1px solid ${C.b1}`,
              borderRadius:3, marginBottom:4, fontSize:10, color:C.mid,
            }}>
              <div>{link.from} → {link.to}</div>
              <div style={{ fontSize:9, color:C.static, marginTop:3 }}>{link.mechanism}</div>
              <div style={{ fontSize:9, color:C.dim, marginTop:3 }}>
                evidence: {link.evidence?.length || 0} · confidence: {link.confidence ?? '?'}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function EvidenceBasisView({ items }) {
  const labelColor = (basis) => ({
    EMPIRICAL:     C.sage,
    ANALOG:        C.gold,
    EXTRAPOLATIVE: C.terra,
  }[basis] || C.dim);
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} style={{
          display:'flex', alignItems:'center', gap:10,
          padding:'6px 0', borderBottom: i < items.length - 1 ? `1px solid ${C.b1}` : 'none',
          fontSize:10,
        }}>
          <span style={{
            fontSize:8, padding:'2px 7px', borderRadius:3,
            background:`rgba(255,255,255,.05)`, color:labelColor(it.basis),
            letterSpacing:'0.08em',
          }}>{it.basis}</span>
          <span style={{ color:C.mid }}>{it.id}</span>
        </div>
      ))}
    </div>
  );
}

// PrescriptionsView — Section 7 renderer (Week 12 polish; replaces the
// raw-JSON dump that shipped with the Week 1 scaffold). Respects
// reflexivity mode: when redacted=true, human_application is replaced
// with diagnostic_note. Menu mode shows the menu_note framing.
function PrescriptionsView({ prescriptions, reflexivity }) {
  const labelColor = (basis) => ({
    EMPIRICAL:     C.sage,
    ANALOG:        C.gold,
    EXTRAPOLATIVE: C.terra,
  }[basis] || C.dim);
  const gatingColor = (g) => ({
    still_effective: C.sage,
    too_late:        C.terra,
    sequencing_required: C.gold,
  }[g] || C.dim);
  const mode = reflexivity?.mode_applied || reflexivity?.mode;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {mode === 'diagnostic_only' && (
        <div style={{
          padding:'8px 10px', background:'rgba(196,96,80,.06)',
          border:`1px solid ${C.terra}`, borderRadius:4,
          fontSize:9, color:C.terra, letterSpacing:'0.04em',
        }}>
          ⚠ DIAGNOSTIC-ONLY MODE — human prescriptions redacted; mechanism + ecological solution + evidence basis preserved.
        </div>
      )}
      {prescriptions.map((p, i) => (
        <div key={p.id || i} style={{
          padding:12, background:C.bg,
          border:`1px solid ${C.b1}`, borderRadius:4,
        }}>
          <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8 }}>
            <span style={{
              fontSize:8, padding:'2px 7px', borderRadius:3,
              background:'rgba(255,255,255,.05)', color:labelColor(p.evidence_basis),
              letterSpacing:'0.08em',
            }}>{p.evidence_basis || 'UNTAGGED'}</span>
            <strong style={{ fontSize:11, color:C.text }}>
              {p.mechanism_label || p.mechanism_id}
            </strong>
            {p.gating && p.gating !== 'unknown' && (
              <span style={{
                fontSize:8, padding:'2px 7px', borderRadius:3, marginLeft:'auto',
                background:'rgba(255,255,255,.05)', color:gatingColor(p.gating),
                letterSpacing:'0.06em',
              }}>{p.gating.replace(/_/g, ' ').toUpperCase()}</span>
            )}
            {Number.isFinite(p.confidence) && (
              <span style={{ fontSize:9, color:C.dim }}>conf {p.confidence.toFixed(2)}</span>
            )}
          </div>

          {/* Ecological side — always visible */}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:9, color:C.gold, marginBottom:3, letterSpacing:'0.06em' }}>
              ECOLOGICAL SOLUTION
            </div>
            <div style={{ fontSize:10, color:C.mid, lineHeight:1.55 }}>
              {p.ecological_solution || '(no solution text)'}
            </div>
          </div>

          {/* Human application — redacted in diagnostic_only mode */}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:9, color:p.redacted ? C.terra : C.teal, marginBottom:3, letterSpacing:'0.06em' }}>
              HUMAN APPLICATION {p.redacted && '(REDACTED)'}
            </div>
            <div style={{ fontSize:10, color:p.redacted ? C.dim : C.mid, lineHeight:1.55, fontStyle: p.redacted ? 'italic' : 'normal' }}>
              {p.redacted
                ? (p.diagnostic_note || '[redacted under diagnostic-only mode]')
                : (p.human_application || '(no translation text)')}
            </div>
          </div>

          {/* Menu note — when in menu framing */}
          {p.menu_note && (
            <div style={{
              padding:'6px 8px', background:'rgba(200,169,110,.06)',
              border:`1px solid ${C.gold}`, borderRadius:3, marginBottom:8,
              fontSize:9, color:C.mid, lineHeight:1.5,
            }}>
              {p.menu_note}
            </div>
          )}

          {/* Evidence-basis reasoning */}
          {p.evidence_basis_reasoning && (
            <div style={{ fontSize:9, color:C.dim, marginBottom:6, lineHeight:1.5 }}>
              <strong style={{ color:C.static }}>basis: </strong>{p.evidence_basis_reasoning}
            </div>
          )}

          {/* Gating reason */}
          {p.gating_reason && (
            <div style={{ fontSize:9, color:C.dim, lineHeight:1.5 }}>
              <strong style={{ color:C.static }}>gating: </strong>{p.gating_reason}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function EthicalCheckView({ ec }) {
  if (!ec) return <em style={emStyle}>not provided</em>;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6, fontSize:10 }}>
      <KvRow label="dignity"     value={ec.dignity}     valueColor={ec.dignity === 'preserved' ? C.sage : C.terra} />
      <KvRow label="agency"      value={ec.agency}      valueColor={ec.agency === 'voluntary' ? C.sage : C.terra} />
      <KvRow label="misuse risk" value={ec.misuse_risk} valueColor={ec.misuse_risk === 'low' ? C.sage : ec.misuse_risk === 'medium' ? C.gold : C.terra} />

      {Array.isArray(ec.misuse_audit) && ec.misuse_audit.length > 0 && (
        <div style={{ marginTop:6 }}>
          <div style={{ fontSize:9, color:C.gold, marginBottom:4, letterSpacing:'0.06em' }}>misuse audit</div>
          <ul style={listStyle}>{ec.misuse_audit.map((line, i) => <li key={i}>{line}</li>)}</ul>
        </div>
      )}

      {ec.cultural_humility && (
        <div style={{
          marginTop:6, padding:10, background:'rgba(155,143,196,.06)',
          border:`1px solid ${C.purple}`, borderRadius:4,
          fontSize:10, color:C.mid, lineHeight:1.55,
        }}>
          <div style={{ fontSize:9, color:C.purple, marginBottom:4, letterSpacing:'0.06em' }}>cultural humility</div>
          {ec.cultural_humility}
        </div>
      )}
    </div>
  );
}

function ConfidenceView({ c }) {
  const overallColor = c.overall === 'high' ? C.sage : c.overall === 'medium' ? C.gold : c.overall === 'low' ? C.terra : C.dim;
  return (
    <div>
      <div style={{ fontSize:14, color:overallColor, marginBottom:8, letterSpacing:'0.06em' }}>
        Overall: {c.overall}
      </div>
      {c.breakdown && Object.keys(c.breakdown).length > 0 && (
        <div style={{ marginBottom:8 }}>
          {Object.entries(c.breakdown).map(([k, v]) => (
            <KvRow key={k} label={k} value={String(v)} />
          ))}
        </div>
      )}
      {Array.isArray(c.what_would_change) && c.what_would_change.length > 0 && (
        <div>
          <div style={{ fontSize:9, color:C.gold, marginBottom:4, letterSpacing:'0.06em' }}>what would change my mind</div>
          <ul style={listStyle}>{c.what_would_change.map((line, i) => <li key={i}>{line}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function ReflexivityView({ r }) {
  if (!r) return <em style={emStyle}>not rated</em>;
  const ratingColor = r.rating === 'HIGH' ? C.terra : r.rating === 'MEDIUM' ? C.gold : C.sage;
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <span style={{
          fontSize:9, padding:'3px 9px', borderRadius:3,
          background:`rgba(255,255,255,.05)`, color:ratingColor,
          letterSpacing:'0.08em',
        }}>RATING: {r.rating}</span>
        <span style={{ fontSize:9, color:C.mid }}>→ output mode: <span style={{ color:C.gold }}>{r.mode}</span></span>
      </div>
      {r.reason && (
        <div style={{
          padding:10, background:C.bg, border:`1px solid ${C.b1}`,
          borderRadius:4, fontSize:10, color:C.mid, lineHeight:1.55,
        }}>
          {r.reason}
        </div>
      )}
    </div>
  );
}

function KvRow({ label, value, valueColor }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:10 }}>
      <span style={{ color:C.dim }}>{label}</span>
      <span style={{ color:valueColor || C.mid }}>{value}</span>
    </div>
  );
}

function formatValue(v) {
  if (typeof v !== 'number') return String(v);
  if (v >= 1e6) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (Math.abs(v) >= 100) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return v.toLocaleString('en-US', { maximumFractionDigits: 3 });
}

const thStyle = { padding:'6px 10px', textAlign:'left', fontSize:8, color:C.dim, letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:'normal' };
const tdStyle = { padding:'6px 10px', color:C.mid, fontSize:10 };

function OutputHeader({ output }) {
  return (
    <div style={{
      padding:'10px 14px', background:C.s1, border:`1px solid ${C.b2}`,
      borderRadius:5, display:'flex', alignItems:'center', gap:18,
      fontFamily:F.mono, fontSize:10,
    }}>
      <span style={{ color:C.gold }}>⟁ Output</span>
      <span style={{ color:C.dim }}>scenario:</span>
      <span style={{ color:C.text }}>{output._meta?.scenario_id}</span>
      <span style={{ color:C.dim }}>·</span>
      <span style={{ color:C.dim }}>runners:</span>
      <span style={{ color:C.text }}>{output._meta?.runners_used?.join(', ')}</span>
      <span style={{ color:C.dim }}>·</span>
      <span style={{ color:C.dim }}>runtime:</span>
      <span style={{ color:C.text }}>{output._meta?.runtime_ms}ms</span>
    </div>
  );
}

function OutputSection({ num, title, arrives, children }) {
  return (
    <div style={{
      background:C.s1, border:`1px solid ${C.b2}`, borderRadius:5,
      overflow:'hidden',
    }}>
      <div style={{
        padding:'8px 14px', borderBottom:`1px solid ${C.b1}`,
        display:'flex', alignItems:'center', justifyContent:'space-between',
        background:C.s2,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{
            fontSize:9, color:C.dim, fontFamily:F.mono,
            background:C.bg, padding:'1px 6px', borderRadius:3,
            border:`1px solid ${C.b1}`,
          }}>
            {String(num).padStart(2, '0')}
          </span>
          <span style={{ fontSize:11, color:C.mid, fontFamily:F.mono }}>{title}</span>
        </div>
        <span style={{ fontSize:8, color:C.static, fontFamily:F.mono, letterSpacing:'0.08em' }}>
          arrives: {arrives}
        </span>
      </div>
      <div style={{ padding:12 }}>
        {children}
      </div>
    </div>
  );
}

const preStyle = {
  fontFamily:F.mono, fontSize:9, color:C.mid, margin:0,
  whiteSpace:'pre-wrap', lineHeight:1.55, maxHeight:240, overflow:'auto',
};
const listStyle = { fontFamily:F.mono, fontSize:9, color:C.mid, margin:0, paddingLeft:18, lineHeight:1.7 };
const emStyle   = { fontFamily:F.mono, fontSize:9, color:C.static };
